import * as Form from "@radix-ui/react-form";
import React, { useEffect, useState, lazy, Suspense, useRef, use, useCallback, useMemo } from "react";
import {
  Flex,
  Separator,
  Text,
  Button,
  Link,
  Tooltip,
  TextField,
  Tabs,
  CheckboxGroup,
  TextArea,
  Checkbox,
  Popover,
  IconButton,
  RadioGroup,
  HoverCard,
  Box,
  Badge,
  Spinner,
  AlertDialog,
} from "@radix-ui/themes";
import { UploadIcon, InfoCircledIcon, ExclamationTriangleIcon, PlusIcon, Cross1Icon, ArrowLeftIcon, DotsHorizontalIcon, TrashIcon } from "@radix-ui/react-icons";
import { ArrowTopRightOnSquareIcon, ComputerDesktopIcon, DevicePhoneMobileIcon } from "@heroicons/react/16/solid";
import { IPhoneMockup } from "react-device-mockup";
import { debounce } from "lodash";
import {
  useGetLocationGuestPortalSettings,
  useGetBrandGuestPortalSettings,
  useUpdateLocationGuestPortalSettings,
  useUpdateBrandGuestPortalSettings,
  useImageUpload,
  useGetGuestPortalSettingsBanner,
  useUpdateGuestPortalSettingsBanner,
  useGetGuestPortalSettingsLinks,
  useUpdateGuestPortalSettingsLinks,
  useCreateGuestPortalSettingsBanner,
  useCreateGuestPortalSettingsLinks,
  useDeleteGuestPortalSettingsLinks,
  useGetAirshipStatus,
  useGetOrganisationDetails,
  useLiveAccessPoints,
  useGetPlansGuestPortalSettings,
  useUpdatePlanGuestPortalSettings,
  useDeletePlanGuestPortalSettings,
  useGetGuestPortalPeriods,
  useGetPeriodGuestPortalSettings,
} from "hooks";
import { useToast } from "../../context/ToastContext";
import { ToastAlert } from "components";
import { useProfileContext, useLocationContext } from "context";
import { useParams, useLocation as useRouterLocation, useNavigate } from "react-router-dom";
import { DatePickerWithRange } from "components/datepicker/DatePickerWithRange";

const CaptivePortalMockup = lazy(() => import("./CaptivePortalMockup"));

// File size validation helper function
const validateFileSize = (file) => {
  const maxSizeInBytes = 1048576; // 1MB
  if (file && file.size > maxSizeInBytes) {
    return false;
  }
  return true;
};

// Edit plan popover component
const EditPlanPopover = ({
  isOpen,
  onOpenChange,
  editPlanTitle,
  setEditPlanTitle,
  editPlanStartDate,
  setEditPlanStartDate,
  editPlanEndDate,
  setEditPlanEndDate,
  onUpdatePlan,
  isUpdating,
  children,
  validationError,
  setValidationError,
  onDeletePlan,
  hasChanges,
  isDeleteAlertOpen,
  setIsDeleteAlertOpen,
  isDeletingPlan,
  isActivePlan,
}) => {
  // Handle date range changes from the DatePickerWithRange component
  const handleDateRangeChange = useCallback(
    (dateRange) => {
      if (dateRange?.from && dateRange?.to) {
        const formatDateForBackend = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };

        const startDate = formatDateForBackend(dateRange.from);
        const endDate = formatDateForBackend(dateRange.to);

        setEditPlanStartDate(startDate);
        setEditPlanEndDate(endDate);

        if (setValidationError) {
          setValidationError("");
        }
      } else {
        setEditPlanStartDate("");
        setEditPlanEndDate("");
      }
    },
    [setEditPlanStartDate, setEditPlanEndDate, setValidationError]
  );

  // Convert current dates to the format expected by DatePickerWithRange
  const currentDateRange = useMemo(() => {
    return editPlanStartDate && editPlanEndDate
      ? {
          from: new Date(editPlanStartDate),
          to: new Date(editPlanEndDate),
        }
      : { from: null, to: null };
  }, [editPlanStartDate, editPlanEndDate]);

  const handleTitleChange = useCallback(
    (e) => {
      setEditPlanTitle(e.target.value);
      if (setValidationError) {
        setValidationError("");
      }
    },
    [setEditPlanTitle, setValidationError]
  );

  return (
    <>
      <Popover.Root open={isOpen} onOpenChange={onOpenChange}>
        <Popover.Trigger asChild>{children}</Popover.Trigger>
        <Popover.Content side="bottom" align="start" width="296px">
          <Flex gap="4" direction="column">
            <Flex style={{ alignItems: "flex-start", justifyContent: "space-between" }}>
              <Text size="3" weight="medium">
                Edit portal
              </Text>
              <IconButton variant="ghost" color="gray" size="2" onClick={() => onOpenChange(false)}>
                <Cross1Icon width={16} height={16} />
              </IconButton>
            </Flex>
            <Flex direction="column" gap="2">
              <Flex direction="column" gap="1" style={{ alignItems: "flex-start" }}>
                <Text size="2" style={{ alignSelf: "stretch" }}>
                  Portal name
                </Text>
                <TextField.Root size="2" weight="medium" style={{ width: "100%" }} value={editPlanTitle} onChange={handleTitleChange} />
              </Flex>
              <Flex direction="column" gap="1" style={{ alignItems: "flex-start" }}>
                <Text size="2" style={{ alignSelf: "stretch" }}>
                  Date range
                </Text>
                <DatePickerWithRange
                  value={currentDateRange}
                  onDateChange={handleDateRangeChange}
                  disableBeforeDate={new Date()} // Disable past dates
                  numberOfMonths={1}
                  allowFutureDates={true}
                  className="w-full"
                />
              </Flex>
              {validationError && (
                <Flex direction="column" gap="1">
                  <Text size="2" color="red" style={{ fontWeight: 500 }}>
                    {validationError}
                  </Text>
                </Flex>
              )}
            </Flex>
            <Flex direction="column" gap="2" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
              <Button
                variant="solid"
                style={{ alignSelf: "stretch" }}
                disabled={isUpdating || !editPlanTitle || !editPlanStartDate || !editPlanEndDate || !!validationError || !hasChanges}
                loading={isUpdating}
                onClick={onUpdatePlan}
              >
                Save changes
              </Button>
              {onDeletePlan && (
                <Button variant="soft" color="red" style={{ alignSelf: "stretch" }} disabled={isDeletingPlan} onClick={() => setIsDeleteAlertOpen(true)}>
                  Delete portal
                </Button>
              )}
            </Flex>
          </Flex>
        </Popover.Content>
      </Popover.Root>
      {/* Delete Confirmation Alert Dialog */}
      <AlertDialog.Root open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialog.Content maxWidth="450px">
          <AlertDialog.Title>{isActivePlan ? "Delete Active Portal" : "Delete Planned Portal"}</AlertDialog.Title>
          <AlertDialog.Description size="2">{isActivePlan ? "This cannot be undone. Your main portal will be set to active." : "This cannot be undone."}</AlertDialog.Description>

          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray" disabled={isDeletingPlan}>
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button variant="solid" color="red" loading={isDeletingPlan} onClick={onDeletePlan}>
                {isActivePlan ? "Deactivate & Delete" : "Delete"}
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </>
  );
};

const GuestPortal = () => {
  const params = useParams();
  const routerLocation = useRouterLocation();
  const navigate = useNavigate();

  // Track form state changes
  const [isDirty, setIsDirty] = useState(false);
  const [isInitialSetupComplete, setIsInitialSetupComplete] = useState(false);

  // Determine if we're viewing a brand or location portal
  const isBrandRoute = routerLocation.pathname.includes("/portal-brand/");
  const isLocationRoute = routerLocation.pathname.includes("/portal-location/");

  // Get the ID from the URL parameters (either brandId or locationId)
  const paramId = isBrandRoute ? parseInt(params.brandId) : parseInt(params.locationId);

  // Get the brand info from router state
  const { isBrand, brandId, brandName, brandLocationCount, locationName, locationAddress, planId } = routerLocation.state || {};
  // Get location context for additional location data if needed
  const { locationsData } = useLocationContext();
  const { liveAccessPointsStatus } = useLiveAccessPoints(locationsData);
  const { userProfile } = useProfileContext();

  // State to store the effective locationId we'll use for fetching settings
  const [effectiveLocationId, setEffectiveLocationId] = useState(null);
  const [useBrandSettings, setUseBrandSettings] = useState(false);
  // Find a location ID for the brand route
  useEffect(() => {
    const { forceLocationId } = routerLocation.state || {};

    console.log("GuestPortal - forceLocationId:", forceLocationId);
    console.log("GuestPortal - routerLocation.state:", routerLocation.state);

    if (forceLocationId) {
      // Use the forced location ID (for main plan editing from brand route)
      console.log("Setting effectiveLocationId to forceLocationId:", forceLocationId);
      setEffectiveLocationId(forceLocationId);
      setUseBrandSettings(false);
    } else if (isBrandRoute && locationsData) {
      // Find the first location that belongs to this brand
      const locationForBrand = locationsData.find((location) => location.brand_id === paramId);
      if (locationForBrand) {
        setEffectiveLocationId(locationForBrand.id);
        setUseBrandSettings(false);
      } else {
        // If no location found for this brand then we use the brandId directly
        setEffectiveLocationId(paramId);
        setUseBrandSettings(true);
      }
    } else if (isLocationRoute) {
      // For location route, use the locationId directly
      setEffectiveLocationId(paramId);
      setUseBrandSettings(false);
    }
  }, [isBrandRoute, isLocationRoute, paramId, locationsData, navigate, routerLocation.state]);

  const { data: orgDetails } = useGetOrganisationDetails(userProfile.organisation_id);

  const { data: airshipStatusData, isLoading: isStatusLoading } = useGetAirshipStatus(userProfile.organisation_id);
  const isAirshipActive = () => {
    if (isStatusLoading) return false;
    return airshipStatusData?.data?.is_active === true;
  };

  const { setToastMessage } = useToast();
  const [view, setView] = useState("mobile");
  const [screenWidthMobile, setScreenWidthMobile] = useState(250); // Default screenWidth for IPhoneMockup
  const [scaleFactorMobile, setScaleFactorMobile] = useState(1); // Default scale factor for IPhoneMockup
  const [isSaveChangesLoading, setIsSaveChangesLoading] = useState(false);

  // Mobile view calculations for IPhoneMockup
  useEffect(() => {
    const handleResize = debounce(() => {
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      // Mobile view calculations
      const aspectRatio = 19.5 / 9;
      const maxDesiredWidth = 390; // The maximum desired screenWidth for IPhoneMockup
      const offset = 250;
      const availableHeight = windowHeight - offset;

      const calculatedScreenWidth = Math.min(
        maxDesiredWidth,
        availableHeight / aspectRatio,
        windowWidth // Ensure it doesn't exceed the viewport width
      );
      const calculatedScaleFactor = calculatedScreenWidth / maxDesiredWidth;

      setScreenWidthMobile(calculatedScreenWidth);
      setScaleFactorMobile(calculatedScaleFactor);
    }, 100);

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { data: brandGuestPortalSettings } = useGetBrandGuestPortalSettings(
    useBrandSettings ? effectiveLocationId : null, // Only fetch if useBrandSettings is true
    planId // Pass planId if available
  );
  const { data: locationGuestPortalSettings } = useGetLocationGuestPortalSettings(
    !useBrandSettings ? effectiveLocationId : null, // Only fetch if useBrandSettings is false
    planId // Pass planId if available
  );

  // Fetching guest portal settings based on brand or location
  const guestPortalSettings = useBrandSettings ? brandGuestPortalSettings : locationGuestPortalSettings;
  const shouldFetchBanner = guestPortalSettings?.data?.id && guestPortalSettings?.data?.banner_flag;
  const { data: guestPortalSettingsBanner } = useGetGuestPortalSettingsBanner(shouldFetchBanner ? guestPortalSettings.data.id : null);
  const { data: guestPortalSettingsLinks } = useGetGuestPortalSettingsLinks(guestPortalSettings?.data?.id);
  const { data: plansData } = useGetPlansGuestPortalSettings(brandId, effectiveLocationId);
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    setPlan(plansData?.data?.find((p) => p.id === planId));
  }, [plansData, planId]);

  // Period selection state and hooks
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const { data: periodsData, isLoading: isLoadingPeriods } = useGetGuestPortalPeriods(plan?.id || null);
  const { data: periodGuestPortalSettings } = useGetPeriodGuestPortalSettings(selectedPeriod?.guest_portal_setting || null);

  // Set default selected period when periods data changes
  useEffect(() => {
    if (periodsData?.data?.length > 0) {
      // Sort periods chronologically by start_time
      const sortedPeriods = [...periodsData.data].sort((a, b) => {
        const aHour = parseInt(a.start_time.split(":")[0], 10);
        const bHour = parseInt(b.start_time.split(":")[0], 10);
        return aHour - bHour;
      });

      // Select the first period by default
      setSelectedPeriod(sortedPeriods[0]);
    } else {
      setSelectedPeriod(null);
    }
  }, [periodsData, plan]);

  // Handle period selection
  // Track changes for each period
  const [periodChanges, setPeriodChanges] = useState({});

  // Handle period selection
  const handlePeriodClick = (period) => {
    // If initial setup isn't complete yet, just switch periods without saving changes
    if (!isInitialSetupComplete) {
      setSelectedPeriod(period);
      return;
    }

    // If there are unsaved changes and we're switching to a different period
    if (isDirty && selectedPeriod?.id !== period.id) {
      // Save current changes to periodChanges map
      setPeriodChanges((prev) => ({
        ...prev,
        [selectedPeriod.id]: {
          logoPreview,
          logoFileName,
          backgroundPreview,
          backgroundFileName,
          backgroundOverlayColor,
          backgroundOverlayOpacity,
          buttonColor,
          buttonTextColor,
          message,
          privacyPolicy,
          redirectUrl,
          emailFlag,
          ageFlag,
          dobFlag,
          genderFlag,
          facebookFlag,
          showLinks,
          showBanner,
          landingType,
          segmentView,
          privacyType,
          bannerTitle,
          bannerDescription,
          bannerLinkURL,
          bannerImage: {
            preview: bannerImage.preview,
            fileName: bannerImage.fileName,
            file: bannerImage.file,
            isDeleted: bannerImage.isDeleted,
          },
          links: [...links],
          locationLinkUrls: { ...locationLinkUrls },
        },
      }));
    }

    // If we're switching back to a period with saved changes, restore them
    if (periodChanges[period.id]) {
      const changes = periodChanges[period.id];
      // Restore all saved changes
      setLogoPreview(changes.logoPreview);
      setLogoFileName(changes.logoFileName);
      setBackgroundPreview(changes.backgroundPreview);
      setBackgroundFileName(changes.backgroundFileName);
      setBackgroundOverlayColor(changes.backgroundOverlayColor);
      setBackgroundOverlayOpacity(changes.backgroundOverlayOpacity);
      setButtonColor(changes.buttonColor);
      setButtonTextColor(changes.buttonTextColor);
      setMessage(changes.message);
      setPrivacyPolicy(changes.privacyPolicy);
      setRedirectUrl(changes.redirectUrl);
      setEmailFlag(changes.emailFlag);
      setAgeFlag(changes.ageFlag);
      setDobFlag(changes.dobFlag);
      setGenderFlag(changes.genderFlag);
      setFacebookFlag(changes.facebookFlag);
      setShowLinks(changes.showLinks);
      setShowBanner(changes.showBanner);
      setLandingType(changes.landingType);
      setSegmentView(changes.segmentView);
      setPrivacyType(changes.privacyType);
      setBannerTitle(changes.bannerTitle);
      setBannerDescription(changes.bannerDescription);
      setBannerLinkURL(changes.bannerLinkURL);

      // Special handling for banner image
      if (changes.bannerImage.preview) {
        bannerImage.setExistingImage(changes.bannerImage.preview, changes.bannerImage.fileName);
        bannerImage.file = changes.bannerImage.file;
        bannerImage.isDeleted = changes.bannerImage.isDeleted;
      } else {
        bannerImage.handleClear();
      }

      setLinks(changes.links);
      setLocationLinkUrls(changes.locationLinkUrls);

      // We temporarily set isDirty to false to prevent re-initialization from useEffect
      // and then restore it after the period change
      setIsDirty(true);
    } else {
      // For periods without saved changes, we'll let the useEffect initialize them
      // from API data, so we need to ensure isDirty is false
      setIsDirty(false);
    }

    setSelectedPeriod(period);
  };

  // Use period-specific settings when available, otherwise fall back to plan settings
  // This is currently unused but kept for potential future use
  // const settingsToUse = selectedPeriod && periodGuestPortalSettings?.data ? periodGuestPortalSettings.data : guestPortalSettings?.data;

  // Use location-based mutation hooks
  const [emailFlag, setEmailFlag] = useState(null);
  const [ageFlag, setAgeFlag] = useState(null);
  const [genderFlag, setGenderFlag] = useState(null);
  const [dobFlag, setDobFlag] = useState(null);
  const [facebookFlag, setFacebookFlag] = useState(null);
  const [ firstNameFlag, setFirstNameFlag ] = useState(null);
  const [ lastNameFlag, setLastNameFlag ] = useState(null);
  const [ phoneNumberFlag, setPhoneNumberFlag ] = useState(null);

  const handleCpFormFieldsChange = (values) => {
    const ageChecked = values.includes("age");

    setEmailFlag(values.includes("email"));
    setAgeFlag(ageChecked);
    setGenderFlag(values.includes("gender"));
    setFacebookFlag(values.includes("facebook"));
    setFirstNameFlag(values.includes("first-name"));
    setLastNameFlag(values.includes("last-name"));
    setPhoneNumberFlag(values.includes("phone-number"));

    if (!ageChecked) {
      setDobFlag(false); // Reset DOB if age is unchecked
    }
    
    setIsDirty(true); // Mark form as dirty on change
  };

  // Mutation hooks for guest portal settings
  const { mutate: updateGuestPortalSettings } = useUpdateLocationGuestPortalSettings();
  const { mutate: updateBrandGuestPortalSettings } = useUpdateBrandGuestPortalSettings();
  const { mutate: updateGuestPortalSettingsBanner } = useUpdateGuestPortalSettingsBanner();
  const { mutate: createGuestPortalSettingsBanner } = useCreateGuestPortalSettingsBanner();
  const { mutate: updateGuestPortalSettingsLinks } = useUpdateGuestPortalSettingsLinks();
  const { mutate: createGuestPortalSettingsLinks } = useCreateGuestPortalSettingsLinks();
  const { mutate: deleteGuestPortalSettingsLinks } = useDeleteGuestPortalSettingsLinks();

  const [logoUpload, setLogoUpload] = useState(null); // Logo image upload
  const [logoPreview, setLogoPreview] = useState(null); // Logo image URL for preview
  const [logoFileName, setLogoFileName] = useState(""); // Logo image file name
  const logoInputRef = useRef(null);

  const [backgroundImageUpload, setBackgroundImageUpload] = useState(null); // Background image upload
  const [backgroundPreview, setBackgroundPreview] = useState(null); // Background image URL for preview
  const [backgroundFileName, setBackgroundFileName] = useState(""); // Background image file name
  const backgroundInputRef = useRef(null);

  function colorConversion(inputColour, opacity) {
    const alpha = Math.round(opacity * 2.55)
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
    return `${inputColour}${alpha}`;
  }

  const [backgroundOverlayColor, setBackgroundOverlayColor] = useState("#000000"); // Default Background overlay color
  const [backgroundOverlayOpacity, setBackgroundOverlayOpacity] = useState(50); // Default Background overlay opacity

  useEffect(() => {
    colorConversion(backgroundOverlayColor, backgroundOverlayOpacity);
  }, [backgroundOverlayColor, backgroundOverlayOpacity]);

  const [buttonColor, setButtonColor] = useState("#3807D9");
  function getButtonTextColorBasedOnBackground(buttonBackgroundColor) {
    buttonBackgroundColor = buttonBackgroundColor.replace("#", "");
    // Converting the hex color to RGB
    const r = parseInt(buttonBackgroundColor.substring(0, 2), 16) / 255;
    const g = parseInt(buttonBackgroundColor.substring(2, 4), 16) / 255;
    const b = parseInt(buttonBackgroundColor.substring(4, 6), 16) / 255;

    // Calculating luminance using the formula
    const luminance =
      0.2126 * (r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)) +
      0.7152 * (g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)) +
      0.0722 * (b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4));

    // If luminance is higher than 0.5, using black text; otherwise, white text
    return luminance > 0.5 ? "#000000" : "#FFFFFF";
  }

  const [buttonTextColor, setButtonTextColor] = useState(getButtonTextColorBasedOnBackground(buttonColor));
  useEffect(() => {
    setButtonTextColor(getButtonTextColorBasedOnBackground(buttonColor));
  }, [buttonColor]);

  const [deleteLogo, setDeleteLogo] = useState(false);
  const [deleteBackgroundImage, setDeleteBackgroundImage] = useState(false);

  const [message, setMessage] = useState("");
  const [privacyPolicy, setPrivacyPolicy] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");

  const [segmentView, setSegmentView] = useState("page");
  const [activeTab, setActiveTab] = useState("portal");

  const [showLinks, setShowLinks] = useState(false); // Link flag
  const [links, setLinks] = useState([]);
  const [isLinksDirty, setIsLinksDirty] = useState(false);
  const [deletedLinks, setDeletedLinks] = useState([]);
  const [currentEditingLink, setCurrentEditingLink] = useState(null);
  const [isAddingLink, setIsAddingLink] = useState(false);
  const linkIconUpload = useImageUpload(null, null);
  const [linkIconUploadFailed, setLinkIconUploadFailed] = useState(false);
  const [dirtyLinkIds, setDirtyLinkIds] = useState(new Set());
  const [locationLinkUrls, setLocationLinkUrls] = useState({});
  const brandLocations = locationsData ? locationsData.filter((loc) => loc.brand_id === (isBrandRoute ? paramId : null)) : [];
  const canHaveUniqueUrls = isBrandRoute && brandLocations?.length > 1;

  // Open link editor - works for both new and existing links
  const openLinkEditor = (link, index) => {
    if (link) {
      // Editing existing link
      setCurrentEditingLink({ ...link, index });
      setIsAddingLink(false);

      // Set the icon preview if it exists
      if (link.icon) {
        linkIconUpload.setExistingImage(link.icon, link.icon_filename || "");
      } else {
        linkIconUpload.handleClear();
      }
    } else {
      // Adding new link
      setCurrentEditingLink({
        label: "",
        link_url: "",
        icon: null,
        icon_filename: null,
        unique_url_flag: false,
      });
      setIsAddingLink(true);
      linkIconUpload.handleClear();
    }
  };

  // Close link editor and reset state
  const closeLinkEditor = () => {
    setCurrentEditingLink(null);
    setIsAddingLink(false);
    linkIconUpload.handleClear();
  };

  // Save link changes
  const saveLinkChanges = () => {
    if (isAddingLink) {
      // Add new link
      const newLink = {
        ...currentEditingLink,
        icon: linkIconUpload.preview,
        icon_filename: linkIconUpload.fileName,
        icon_file: linkIconUpload.file,
      };
      setLinks([...links, newLink]);
      setIsLinksDirty(true);
      setIsDirty(true); // Mark form as dirty immediately
    } else {
      // Update existing link
      const updatedLinks = [...links];
      updatedLinks[currentEditingLink.index] = {
        ...currentEditingLink,
        icon: linkIconUpload.preview,
        icon_filename: linkIconUpload.fileName,
        icon_file: linkIconUpload.file,
        isDeleted: linkIconUpload.isDeleted,
      };
      setLinks(updatedLinks);
      setIsLinksDirty(true);
      setDirtyLinkIds((s) => new Set(s).add(currentEditingLink.id));
      setIsDirty(true); // Mark form as dirty immediately
    }
    closeLinkEditor();
  };

  // Handle link removal
  const handleLinkRemove = (index) => {
    if (links[index].id) {
      setDeletedLinks([...deletedLinks, links[index].id]);
    }
    const updatedLinks = links.filter((_, i) => i !== index);
    setLinks(updatedLinks);
    setIsLinksDirty(true);
    setIsDirty(true); // Mark form as dirty immediately
    closeLinkEditor();
  };

  // Update current editing link field
  const updateLinkField = (field, value) => {
    setCurrentEditingLink({
      ...currentEditingLink,
      [field]: value,
    });
    setIsDirty(true); // Mark form as dirty immediately when link fields are updated
  };

  const [showBanner, setShowBanner] = useState(false); // Banner flag
  const [bannerTitle, setBannerTitle] = useState("");
  const [bannerDescription, setBannerDescription] = useState("");
  const [bannerLinkURL, setBannerLinkURL] = useState("");
  const bannerImage = useImageUpload(guestPortalSettingsBanner?.data?.image || null, guestPortalSettingsBanner?.data?.image_filename || "");

  const [privacyType, setPrivacyType] = useState("auto"); // Default to 'auto' for privacy type
  const [landingType, setLandingType] = useState("page"); // Default to 'page' for landing type

  // Helper function to add https:// to a URL if it doesn't have it
  const addHttpsIfNeeded = (url) => {
    if (url && !url.match(/^https?:\/\//)) {
      return `https://${url}`;
    }
    return url;
  };

  // Fetching the Guest Portal settings
  useEffect(() => {
    if (guestPortalSettings?.data) {
      // Setting the initial values for the form fields
      setLogoPreview(guestPortalSettings.data.logo);
      setLogoFileName(guestPortalSettings.data.logo_filename);
      setBackgroundPreview(guestPortalSettings.data.background_image);
      setBackgroundFileName(guestPortalSettings.data.background_image_filename);
      setBackgroundOverlayColor(guestPortalSettings.data.background_overlay_colour);
      setBackgroundOverlayOpacity(guestPortalSettings.data.background_overlay_opacity * 100);
      setButtonColor(guestPortalSettings.data.button_colour);
      setButtonTextColor(guestPortalSettings.data.button_text_colour);
      setMessage(guestPortalSettings.data.message);
      setPrivacyPolicy(guestPortalSettings.data.privacy_policy);
      setRedirectUrl(guestPortalSettings.data.redirect_url);
      setEmailFlag(guestPortalSettings.data.email_flag);
      setAgeFlag(guestPortalSettings.data.age_flag);
      setDobFlag(guestPortalSettings.data.dob_flag);
      setGenderFlag(guestPortalSettings.data.gender_flag);
      setFirstNameFlag(guestPortalSettings.data.first_name_flag);
      setLastNameFlag(guestPortalSettings.data.last_name_flag);
      setPhoneNumberFlag(guestPortalSettings.data.phone_number_flag);
      setFacebookFlag(guestPortalSettings.data.facebook_flag);
      setShowLinks(guestPortalSettings.data.links_flag);
      setShowBanner(guestPortalSettings.data.banner_flag);
      setLandingType(guestPortalSettings.data.landing_type || "page");
      setSegmentView(guestPortalSettings.data.landing_type || "page");
      setPrivacyType(guestPortalSettings.data.privacy_type || "auto");
      // Setting the existing values for the banner if it exists
      if (guestPortalSettingsBanner?.data) {
        setBannerTitle(guestPortalSettingsBanner.data.title);
        setBannerDescription(guestPortalSettingsBanner.data.description);
        setBannerLinkURL(guestPortalSettingsBanner.data.banner_url);
      }
      // Setting the existing values for the links if they exist
      if (guestPortalSettingsLinks?.data) {
        setLinks(guestPortalSettingsLinks.data);

        const initUrls = {};
        guestPortalSettingsLinks.data.forEach((link) => {
          if (link.unique_url_flag) {
            initUrls[link.id] = link.location_link_urls || {};
          }
        });
        setLocationLinkUrls(initUrls);
      }
      resetForm();
    }
  }, [guestPortalSettings?.data, guestPortalSettingsBanner?.data, guestPortalSettingsLinks?.data]);

  // Update form fields when selected period changes and period-specific settings are available
  useEffect(() => {
    if (selectedPeriod && periodGuestPortalSettings?.data) {
      const fullData = periodGuestPortalSettings.data;

      // Handle different data structures - the backend returns { settings, banners, links }
      const settings = fullData.settings || fullData; // Handle both data structures
      const banners = fullData.banners || [];
      const links = fullData.links || [];

      // Only update form fields with period-specific settings when:
      // 1. It's the initial setup, or
      // 2. The form is not dirty (no unsaved changes)
      if (!isInitialSetupComplete || !isDirty) {
        // If this is the initial setup, mark it as complete after this run
        if (!isInitialSetupComplete) {
          setIsInitialSetupComplete(true);
        }

        // First reset all fields to empty - this is crucial to prevent persistence between periods
        setMessage("");
        setPrivacyPolicy("");
        setRedirectUrl("");
        setBannerTitle("");
        setBannerDescription("");
        setBannerLinkURL("");
        bannerImage.handleClear();

        // Then set the period-specific values, only if they exist
        setLogoPreview(settings.logo || null);
        setLogoFileName(settings.logo_filename || "");
        setBackgroundPreview(settings.background_image || null);
        setBackgroundFileName(settings.background_image_filename || "");
        setBackgroundOverlayColor(settings.background_overlay_colour || "#000000");
        setBackgroundOverlayOpacity((settings.background_overlay_opacity ?? 0.5) * 100);
        setButtonColor(settings.button_colour || "#3807D9");
        setButtonTextColor(settings.button_text_colour || getButtonTextColorBasedOnBackground(settings.button_colour || "#3807D9"));

        // Only set fields if they exist in the period settings, don't fall back to plan settings
        if (settings.message) setMessage(settings.message);
        if (settings.privacy_policy) setPrivacyPolicy(settings.privacy_policy);
        if (settings.redirect_url) setRedirectUrl(settings.redirect_url);

        // Boolean flags - set to false if not defined
        setEmailFlag(settings.email_flag ?? false);
        setAgeFlag(settings.age_flag ?? false);
        setDobFlag(settings.dob_flag ?? false);
        setGenderFlag(settings.gender_flag ?? false);
        setFacebookFlag(settings.facebook_flag ?? false);
        setShowLinks(settings.links_flag ?? false);
        setShowBanner(settings.banner_flag ?? false);

        // Landing type settings
        setLandingType(settings.landing_type || "page");
        setSegmentView(settings.landing_type || "page");
        setPrivacyType(settings.privacy_type || "auto");

        // Update banner data for period-specific settings
        if (banners && banners.length > 0) {
          const banner = banners[0]; // Use first banner
          setBannerTitle(banner.title || "");
          setBannerDescription(banner.description || "");
          setBannerLinkURL(banner.banner_url || "");
          bannerImage.setExistingImage(banner.image || null, banner.image_filename || "");
        }

        // Update links data for period-specific settings
        if (links && links.length > 0) {
          setLinks(links);
          const initUrls = {};
          links.forEach((link) => {
            if (link.unique_url_flag) {
              initUrls[link.id] = link.location_link_urls || {};
            }
          });
          setLocationLinkUrls(initUrls);
        } else {
          setLinks([]);
          setLocationLinkUrls({});
        }
      }
    } else if (!selectedPeriod && guestPortalSettings?.data) {
      // Reset to plan-level settings when no period is selected, but only if:
      // 1. It's the initial setup, or
      // 2. The form is not dirty (no unsaved changes)
      if (!isInitialSetupComplete || !isDirty) {
        // If this is the initial setup, mark it as complete after this run
        if (!isInitialSetupComplete) {
          setIsInitialSetupComplete(true);
        }

        // First reset all fields to empty
        setMessage("");
        setPrivacyPolicy("");
        setRedirectUrl("");
        setBannerTitle("");
        setBannerDescription("");
        setBannerLinkURL("");
        bannerImage.handleClear();

        // Then set plan-level settings if available
        setLogoPreview(guestPortalSettings.data.logo);
        setLogoFileName(guestPortalSettings.data.logo_filename || "");
        setBackgroundPreview(guestPortalSettings.data.background_image);
        setBackgroundFileName(guestPortalSettings.data.background_image_filename || "");
        setBackgroundOverlayColor(guestPortalSettings.data.background_overlay_colour || "#000000");
        setBackgroundOverlayOpacity((guestPortalSettings.data.background_overlay_opacity || 0.5) * 100);
        setButtonColor(guestPortalSettings.data.button_colour || "#3807D9");
        setButtonTextColor(guestPortalSettings.data.button_text_colour || getButtonTextColorBasedOnBackground(guestPortalSettings.data.button_colour || "#3807D9"));

        // Only set if they exist in plan settings
        if (guestPortalSettings.data.message) setMessage(guestPortalSettings.data.message);
        if (guestPortalSettings.data.privacy_policy) setPrivacyPolicy(guestPortalSettings.data.privacy_policy);
        if (guestPortalSettings.data.redirect_url) setRedirectUrl(guestPortalSettings.data.redirect_url);

        setEmailFlag(guestPortalSettings.data.email_flag ?? false);
        setAgeFlag(guestPortalSettings.data.age_flag ?? false);
        setDobFlag(guestPortalSettings.data.dob_flag ?? false);
        setGenderFlag(guestPortalSettings.data.gender_flag ?? false);
        setFacebookFlag(guestPortalSettings.data.facebook_flag ?? false);
        setShowLinks(guestPortalSettings.data.links_flag ?? false);
        setShowBanner(guestPortalSettings.data.banner_flag ?? false);
        setLandingType(guestPortalSettings.data.landing_type || "page");
        setSegmentView(guestPortalSettings.data.landing_type || "page");
        setPrivacyType(guestPortalSettings.data.privacy_type || "auto");

        // Reset banner and links to plan-level
        if (guestPortalSettingsBanner?.data) {
          setBannerTitle(guestPortalSettingsBanner.data.title || "");
          setBannerDescription(guestPortalSettingsBanner.data.description || "");
          setBannerLinkURL(guestPortalSettingsBanner.data.banner_url || "");
          bannerImage.setExistingImage(guestPortalSettingsBanner.data.image || null, guestPortalSettingsBanner.data.image_filename || "");
        }

        if (guestPortalSettingsLinks?.data) {
          setLinks(guestPortalSettingsLinks.data);
          const initUrls = {};
          guestPortalSettingsLinks.data.forEach((link) => {
            if (link.unique_url_flag) {
              initUrls[link.id] = link.location_link_urls || {};
            }
          });
          setLocationLinkUrls(initUrls);
        } else {
          setLinks([]);
          setLocationLinkUrls({});
        }
      }
    }
  }, [selectedPeriod, periodGuestPortalSettings?.data, guestPortalSettings?.data, guestPortalSettingsBanner?.data, guestPortalSettingsLinks?.data, bannerImage, isInitialSetupComplete]);

  // Handling Logo image upload
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!validateFileSize(file)) {
      setToastMessage("Maximum file size of 1MB");
      return;
    }

    setLogoUpload(file);
    setLogoFileName(file.name);

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
    setToastMessage("Uploaded");
    setDeleteLogo(false);
  };

  const handleLogoClear = () => {
    setLogoUpload(null);
    setLogoPreview(null);
    setLogoFileName("");
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
    setDeleteLogo(true);
  };

  // Triggering the hidden file input click for logo
  const triggerLogoInput = () => {
    if (logoInputRef.current) {
      logoInputRef.current.click();
    }
  };

  // Handling Background image upload
  const handleBackgroundImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!validateFileSize(file)) {
      setToastMessage("Maximum file size of 1MB");
      return;
    }

    setBackgroundImageUpload(file);
    setBackgroundFileName(file.name);

    const reader = new FileReader();
    reader.onloadend = () => {
      setBackgroundPreview(reader.result);
    };
    reader.readAsDataURL(file);
    setToastMessage("Uploaded");
    setDeleteBackgroundImage(false);
  };

  const handleBackgroundImageClear = () => {
    setBackgroundImageUpload(null);
    setBackgroundPreview(null);
    setBackgroundFileName("");
    if (backgroundInputRef.current) {
      backgroundInputRef.current.value = "";
    }
    setDeleteBackgroundImage(true);
  };

  // Triggering the hidden file input click for background image
  const triggerBackgroundInput = () => {
    if (backgroundInputRef.current) {
      backgroundInputRef.current.click();
    }
  };

  // Resetting the form
  const resetForm = () => {
    setLogoUpload(null);
    setLogoPreview(guestPortalSettings.data.logo);
    setLogoFileName(guestPortalSettings.data.logo_filename);
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
    setBackgroundImageUpload(null);
    setBackgroundPreview(guestPortalSettings.data.background_image);
    setBackgroundFileName(guestPortalSettings.data.background_image_filename);
    if (backgroundInputRef.current) {
      backgroundInputRef.current.value = "";
    }
    setBackgroundOverlayColor(guestPortalSettings.data.background_overlay_colour);
    setBackgroundOverlayOpacity(guestPortalSettings.data.background_overlay_opacity * 100);
    setButtonColor(guestPortalSettings.data.button_colour);
    setButtonTextColor(getButtonTextColorBasedOnBackground(guestPortalSettings.data.button_colour));
    setDeleteLogo(false);
    setDeleteBackgroundImage(false);
    setMessage(guestPortalSettings.data.message);
    setPrivacyPolicy(guestPortalSettings.data.privacy_policy);
    setRedirectUrl(guestPortalSettings.data.redirect_url);
    setEmailFlag(guestPortalSettings.data.email_flag);
    setAgeFlag(guestPortalSettings.data.age_flag);
    setDobFlag(guestPortalSettings.data.dob_flag);
    setGenderFlag(guestPortalSettings.data.gender_flag);
    setFirstNameFlag(guestPortalSettings.data.first_name_flag);
    setLastNameFlag(guestPortalSettings.data.last_name_flag);
    setPhoneNumberFlag(guestPortalSettings.data.phone_number_flag);
    setFacebookFlag(guestPortalSettings.data.facebook_flag);
    setShowLinks(guestPortalSettings.data.links_flag);
    setShowBanner(guestPortalSettings.data.banner_flag);
    if (guestPortalSettingsBanner?.data) {
      setBannerTitle(guestPortalSettingsBanner.data.title);
      setBannerDescription(guestPortalSettingsBanner.data.description);
      setBannerLinkURL(guestPortalSettingsBanner.data.banner_url);
      bannerImage.reset();
    }
    if (guestPortalSettingsLinks?.data) {
      setLinks(guestPortalSettingsLinks.data);
    }
    setDeletedLinks([]);
    setIsLinksDirty(false);
    setIsDirty(false);

    // Ensure we've marked initial setup as complete
    if (!isInitialSetupComplete) {
      setIsInitialSetupComplete(true);
    }
  };

  // Updating locations GPS was not immedietely rendering in mockup after saving changes thus..
  const preserveUpdatedState = (state) => {
    // Don't reset visual values, just clear upload states and dirty flags
    setLogoUpload(null);
    setBackgroundImageUpload(null);
    setDeleteLogo(false);
    setDeleteBackgroundImage(false);
    setIsLinksDirty(false);
    setDeletedLinks([]);
    setIsDirty(false);

    // No need to reset visual state values that are rendered in the mockup
    // as they already have the correct values
  };

  // Submitting the form
  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData();

    try {
      setIsSaveChangesLoading(true);

      // Populate form data the same way as before
      if (logoUpload) {
        data.append("logo", logoUpload);
      }
      if (backgroundImageUpload) {
        data.append("background_image", backgroundImageUpload);
      }
      if (backgroundOverlayColor !== guestPortalSettings.data.background_overlay_colour) {
        data.append("background_overlay_colour", backgroundOverlayColor);
      }
      if (Math.round(backgroundOverlayOpacity) !== Math.round(guestPortalSettings.data.background_overlay_opacity * 100)) {
        data.append("background_overlay_opacity", backgroundOverlayOpacity / 100);
      }
      if (buttonColor !== guestPortalSettings.data.button_colour) {
        data.append("button_colour", buttonColor);
      }
      if (buttonTextColor !== guestPortalSettings.data.button_text_colour) {
        data.append("button_text_colour", buttonTextColor);
      }
      if (deleteLogo) {
        data.append("delete_logo", true);
      }
      if (deleteBackgroundImage) {
        data.append("delete_background_image", true);
      }
      if (message !== guestPortalSettings.data.message) {
        data.append("message", message);
      }
      if (privacyPolicy !== guestPortalSettings.data.privacy_policy) {
        data.append("privacy_policy", privacyPolicy);
      }
      if (redirectUrl !== guestPortalSettings.data.redirect_url) {
        data.append("redirect_url", addHttpsIfNeeded(redirectUrl));
      }
      if (emailFlag !== guestPortalSettings.data.email_flag) {
        data.append("email_flag", emailFlag);
      }
      if (ageFlag !== guestPortalSettings.data.age_flag) {
        data.append("age_flag", ageFlag);
      }
      if (dobFlag !== guestPortalSettings.data.dob_flag) {
        data.append("dob_flag", dobFlag);
      }
      if (genderFlag !== guestPortalSettings.data.gender_flag) {
        data.append("gender_flag", genderFlag);
      }
      if (firstNameFlag !== guestPortalSettings.data.first_name_flag) {
        data.append("first_name_flag", firstNameFlag);
      }
      if (lastNameFlag !== guestPortalSettings.data.last_name_flag) {
        data.append("last_name_flag", lastNameFlag);
      }
      if (phoneNumberFlag !== guestPortalSettings.data.phone_number_flag) {
        data.append("phone_number_flag", phoneNumberFlag);
      }
      if (facebookFlag !== guestPortalSettings.data.facebook_flag) {
        data.append("facebook_flag", facebookFlag);
      }
      if (showLinks !== guestPortalSettings.data.links_flag) {
        data.append("links_flag", showLinks);
      }
      if (showBanner !== guestPortalSettings.data.banner_flag) {
        data.append("banner_flag", showBanner);
      }
      if (privacyType !== guestPortalSettings.data.privacy_type) {
        data.append("privacy_type", privacyType);
      }
      if (landingType !== guestPortalSettings.data.landing_type) {
        data.append("landing_type", landingType);
      }

      // Handle brand vs location updates differently
      if (isBrandRoute) {
        if (useBrandSettings) {
          // Brand with no locations - use brand-specific API
          await updateBrandGuestPortalSettings({
            brandId: paramId,
            formData: data,
            planId: planId, // Pass planId if available
          });
        } else {
          // For brand-wide updates, include the brandId
          await updateGuestPortalSettings({
            locationId: effectiveLocationId,
            formData: data,
            brandId: paramId, // This is the brand ID from URL params
            planId: planId, // Pass planId if available
          });
        }
      } else {
        // Regular single location update
        await updateGuestPortalSettings({
          locationId: effectiveLocationId,
          formData: data,
          planId: planId, // Pass planId if available
        });
      }

      // Updating locations GPS was not immedietely rendering in mockup after saving changes thus..
      const updatedState = {
        logo: logoUpload ? URL.createObjectURL(logoUpload) : deleteLogo ? null : logoPreview,
        backgroundImage: backgroundImageUpload ? URL.createObjectURL(backgroundImageUpload) : deleteBackgroundImage ? null : backgroundPreview,
        backgroundOverlayColor,
        backgroundOverlayOpacity,
        buttonColor,
        buttonTextColor,
        message,
        privacyPolicy,
        redirectUrl,
        emailFlag,
        ageFlag,
        dobFlag,
        genderFlag,
        firstNameFlag,
        lastNameFlag,
        phoneNumberFlag,
        facebookFlag,
        showLinks,
        showBanner,
        privacyType,
        landingType,
        bannerTitle,
        bannerDescription,
        bannerLinkURL,
        bannerImage: bannerImage.preview,
        links: [...links],
      };

      // Handle banner updates if banner is enabled
      if (showBanner) {
        try {
          if (guestPortalSettingsBanner?.data) {
            await updateGuestPortalSettingsBanner({
              guestPortalId: guestPortalSettings.data.id,
              formData: {
                title: bannerTitle,
                description: bannerDescription,
                banner_url: addHttpsIfNeeded(bannerLinkURL),
                image: bannerImage.file,
                delete_image: bannerImage.isDeleted,
                ...(isBrandRoute ? { brandId: paramId, planId } : { planId }), // Only send brandId if useBrandSettings is true
              },
            });
          } else {
            await createGuestPortalSettingsBanner({
              guestPortalId: guestPortalSettings.data.id,
              formData: {
                title: bannerTitle,
                description: bannerDescription,
                banner_url: addHttpsIfNeeded(bannerLinkURL),
                image: bannerImage.file,
                delete_image: false,
                ...(isBrandRoute ? { brandId: paramId, planId } : { planId }),
              },
            });
          }
        } catch (error) {
          console.error("Error updating banner:", error);
          return;
        }
      }
      // Handle links updates
      if (showLinks) {
        // Handle link deletions first
        for (const linkID of deletedLinks) {
          await deleteGuestPortalSettingsLinks({
            guestPortalId: guestPortalSettings.data.id,
            linkId: linkID,
            ...(isBrandRoute ? { brandId: paramId, planId } : { planId }),
          });
        }

        // Handle existing links updates sequentially
        const linksToUpdate = links.filter((link) => link.id && dirtyLinkIds.has(link.id));
        for (const link of linksToUpdate) {
          const linksFormData = new FormData();
          linksFormData.append("id", link.id);
          linksFormData.append("label", link.label);
          linksFormData.append("link_url", addHttpsIfNeeded(link.link_url));
          linksFormData.append("unique_url_flag", link.unique_url_flag);
          if (link.unique_url_flag && canHaveUniqueUrls) {
            const rawUrls = locationLinkUrls[link.id] || {};
            const httpsUrls = Object.fromEntries(Object.entries(rawUrls).map(([locId, url]) => [locId, addHttpsIfNeeded(url)]));
            linksFormData.append("location_link_urls", JSON.stringify(httpsUrls));
          }
          if (link.icon_file) {
            linksFormData.append("icon", link.icon_file);
          } else if (link.isDeleted) {
            linksFormData.append("delete_icon", true);
          }
          for (const [key, value] of linksFormData.entries()) {
            console.log(`linksFormData entry "${key}":`, value);
          }
          await updateGuestPortalSettingsLinks({
            guestPortalId: guestPortalSettings.data.id,
            formData: linksFormData,
            ...(isBrandRoute ? { brandId: paramId, planId } : { planId }), // Only send brandId if useBrandSettings is true
          });
        }

        // Create new links without IDs sequentially
        const newLinks = links.filter((link) => !link.id);
        for (const link of newLinks) {
          const linkFormData = new FormData();
          linkFormData.append("label", link.label);
          linkFormData.append("link_url", addHttpsIfNeeded(link.link_url));
          linkFormData.append("unique_url_flag", link.unique_url_flag);
          if (link.unique_url_flag && canHaveUniqueUrls) {
            const rawUrls = locationLinkUrls[link.id] || {};
            const httpsUrls = Object.fromEntries(Object.entries(rawUrls).map(([locId, url]) => [locId, addHttpsIfNeeded(url)]));
            linkFormData.append("location_link_urls", JSON.stringify(httpsUrls));
          }
          if (link.icon_file) {
            linkFormData.append("icon", link.icon_file);
          }
          await createGuestPortalSettingsLinks({
            guestPortalId: guestPortalSettings.data.id,
            formData: linkFormData,
            ...(isBrandRoute ? { brandId: paramId, planId } : { planId }),
          });
        }
      }

      // Updating locations GPS was not immedietely rendering in mockup after saving changes thus..
      // Reset form and show success message
      resetForm();
      setIsLinksDirty(false);
      preserveUpdatedState(updatedState);
      setToastMessage("Changes saved");
    } catch (error) {
      console.error("Error saving changes:", error);
      setToastMessage("Failed to save changes");
    } finally {
      setIsSaveChangesLoading(false);
    }
  };

  const handleInputMessageChange = (value) => {
    setMessage(value);
    setIsDirty(true); // Mark form as dirty on change
  };

  const handlePrivacyPolicyChange = (value) => {
    setPrivacyPolicy(value);
    setIsDirty(true); // Mark form as dirty on change
  };

  const handleInputRedirectUrlChange = (value) => {
    setRedirectUrl(value);
    setIsDirty(true); // Mark form as dirty on change
  };

  // Check if there are any changes in the form fields
  // isDirty is now declared at the top of the component
  useEffect(() => {
    const baseChanges =
      logoUpload !== null ||
      backgroundImageUpload !== null ||
      backgroundOverlayColor !== guestPortalSettings?.data?.background_overlay_colour ||
      Math.round(backgroundOverlayOpacity) !== Math.round(guestPortalSettings?.data?.background_overlay_opacity * 100) ||
      buttonColor !== guestPortalSettings?.data?.button_colour ||
      buttonTextColor !== guestPortalSettings?.data?.button_text_colour ||
      deleteLogo ||
      deleteBackgroundImage ||
      message !== guestPortalSettings?.data?.message ||
      privacyPolicy !== guestPortalSettings?.data?.privacy_policy ||
      redirectUrl !== guestPortalSettings?.data?.redirect_url ||
      emailFlag !== guestPortalSettings?.data?.email_flag ||
      ageFlag !== guestPortalSettings?.data?.age_flag ||
      dobFlag !== guestPortalSettings?.data?.dob_flag ||
      genderFlag !== guestPortalSettings?.data?.gender_flag ||
      firstNameFlag !== guestPortalSettings?.data?.first_name_flag ||
      lastNameFlag !== guestPortalSettings?.data?.last_name_flag ||
      phoneNumberFlag !== guestPortalSettings?.data?.phone_number_flag ||
      facebookFlag !== guestPortalSettings?.data?.facebook_flag ||
      showLinks !== guestPortalSettings?.data?.links_flag ||
      showBanner !== guestPortalSettings?.data?.banner_flag ||
      privacyType !== guestPortalSettings?.data?.privacy_type ||
      landingType !== guestPortalSettings?.data?.landing_type;

    // Banner changes (only check if showBanner is true and server has banner data)
    const bannerChanges =
      showBanner && guestPortalSettingsBanner?.data
        ? bannerTitle !== guestPortalSettingsBanner?.data?.title ||
          bannerDescription !== guestPortalSettingsBanner?.data?.description ||
          bannerLinkURL !== guestPortalSettingsBanner?.data?.banner_url ||
          bannerImage.file !== null ||
          bannerImage.isDeleted
        : false;

    // Links changes (only check if showLinks is true and server has links data)
    const linksChanges = showLinks ? isLinksDirty || deletedLinks.length > 0 || (guestPortalSettingsLinks?.data && links.length !== guestPortalSettingsLinks?.data?.length) : false;

    // Use JSON.stringify only if both objects exist
    const linksContentChanged = showLinks && guestPortalSettingsLinks?.data ? JSON.stringify(links) !== JSON.stringify(guestPortalSettingsLinks?.data) : false;

    const hasChanges = baseChanges || bannerChanges || linksChanges || linksContentChanged;

    setIsDirty(hasChanges);
  }, [
    logoUpload,
    backgroundImageUpload,
    backgroundOverlayColor,
    backgroundOverlayOpacity,
    buttonColor,
    buttonTextColor,
    deleteLogo,
    deleteBackgroundImage,
    message,
    privacyPolicy,
    redirectUrl,
    emailFlag,
    ageFlag,
    dobFlag,
    genderFlag,
    firstNameFlag,
    lastNameFlag,
    phoneNumberFlag,
    facebookFlag,
    showLinks,
    showBanner,
    privacyType,
    landingType,
    bannerTitle,
    bannerDescription,
    bannerLinkURL,
    bannerImage.file,
    bannerImage.isDeleted,
    guestPortalSettings?.data,
    guestPortalSettingsBanner?.data,
    guestPortalSettingsLinks?.data,
    links,
    isLinksDirty,
    deletedLinks,
  ]);

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    d.setDate(d.getDate());
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  function formatDateMinusOne(dateString) {
    if (!dateString) return "";
    const d = new Date(dateString);
    d.setDate(d.getDate() - 1);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  }

  function getPlanBadgeInfo({ plan, plansData, guestPortalSettings, formatDate, formatDateMinusOne }) {
    const now = new Date();
    const nextCustomPlan = plansData?.data?.filter((p) => !p.is_main && new Date(p.start_date) > now)?.sort((a, b) => new Date(a.start_date) - new Date(b.start_date))[0];

    // Green dot logic
    const showGreenDot = (plan && plan.is_main && plan.id === guestPortalSettings?.data.active_plan.id) || (!plan && guestPortalSettings?.data.active_plan.is_main);

    // Badge color and label
    let badgeColor = "gray";
    let badgeLabel = plan?.title || "";
    if (plan?.is_main) {
      badgeColor = "amber";
      badgeLabel = "Main";
    } else if (plan && plan.title === guestPortalSettings?.data.active_plan.title) {
      badgeColor = "grass";
    } else if (!plan && guestPortalSettings?.data.active_plan.is_main) {
      badgeColor = "amber";
      badgeLabel = "Main";
    } else if (!plan) {
      badgeColor = "grass";
      badgeLabel = guestPortalSettings?.data.active_plan.title;
    }

    // Info text (the "tooltip" content in planner)
    let infoText = "";
    if (plan) {
      if (plan.is_main) {
        if (plan.id === guestPortalSettings?.data.active_plan.id) {
          infoText = `Default captive portal\n(Currently active until ${nextCustomPlan ? formatDate(nextCustomPlan.start_date) : "end of schedule"})`;
        } else {
          infoText = `Default captive portal`;
        }
      } else if (plan.title === guestPortalSettings?.data.active_plan.title) {
        infoText = `Currently active until ${formatDateMinusOne(guestPortalSettings?.data.active_plan.end_date)}`;
      } else {
        infoText = `Planned for ${formatDate(plan?.start_date)} - ${formatDateMinusOne(plan?.end_date)}`;
      }
    } else if (guestPortalSettings?.data.active_plan.is_main) {
      infoText = `Default captive portal\n(Currently active until ${nextCustomPlan ? formatDate(nextCustomPlan.start_date) : "end of schedule"})`;
    } else {
      infoText = `Until ${formatDateMinusOne(guestPortalSettings?.data.active_plan.end_date)}`;
    }

    return { showGreenDot, badgeColor, badgeLabel, infoText };
  }

  const [isEditPlanPopoverOpen, setIsEditPlanPopoverOpen] = useState(false);
  const [editPlanTitle, setEditPlanTitle] = useState("");
  const [editPlanStartDate, setEditPlanStartDate] = useState("");
  const [editPlanEndDate, setEditPlanEndDate] = useState("");
  const [editPlanValidationError, setEditPlanValidationError] = useState("");
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);

  const { mutate: updatePlan, isLoading: isUpdatingPlan } = useUpdatePlanGuestPortalSettings();
  const { mutate: deletePlan, isLoading: isDeletingPlan } = useDeletePlanGuestPortalSettings();

  // Utility function for date formatting to avoid timezone issues
  const formatDateForInput = (dateString, adjustment = 0) => {
    const [year, month, day] = dateString.split("-");
    const d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    d.setDate(d.getDate() + adjustment);
    const adjustedYear = d.getFullYear();
    const adjustedMonth = String(d.getMonth() + 1).padStart(2, "0");
    const adjustedDay = String(d.getDate()).padStart(2, "0");
    return `${adjustedYear}-${adjustedMonth}-${adjustedDay}`;
  };

  // Utility function for adjusting end dates for backend storage
  const adjustEndDateForBackend = (endDateString) => {
    const d = new Date(endDateString);
    d.setDate(d.getDate() + 1);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Function to check if two date ranges overlap
  const checkDateRangeOverlap = (start1, end1, start2, end2) => {
    const parseDate = (dateString) => new Date(dateString);

    const startDate1 = parseDate(start1);
    const endDate1 = parseDate(end1);
    const startDate2 = parseDate(start2);
    const endDate2 = parseDate(end2);

    // Allow for end date of one plan to equal start date of another
    if (startDate1.getTime() === endDate2.getTime() || startDate2.getTime() === endDate1.getTime()) {
      return false; // These are consecutive plans, not overlapping
    }

    return startDate1 < endDate2 && startDate2 < endDate1;
  };

  // Function to find overlapping plans
  const findOverlappingPlans = (plans, newStartDate, newEndDate, excludePlanId = null) => {
    return plans.filter((plan) => {
      if (excludePlanId && plan.id === excludePlanId) {
        return false;
      }

      if (plan.is_main) {
        return false;
      }

      return checkDateRangeOverlap(plan.start_date, plan.end_date, newStartDate, newEndDate);
    });
  };

  // Handle opening the edit plan popover
  const handleEditPlanClick = () => {
    if (plan && !plan.is_main) {
      setEditPlanTitle(plan.title);
      setEditPlanStartDate(formatDateForInput(plan.start_date));
      setEditPlanEndDate(formatDateForInput(plan.end_date, -1)); // Show user's original selection
      setEditPlanValidationError("");
      setIsEditPlanPopoverOpen(true);
    }
  };

  // Check if the current plan is active
  const isCurrentPlanActive = () => {
    if (!plan || !guestPortalSettings?.data?.active_plan) return false;
    return plan.id === guestPortalSettings.data.active_plan.id;
  };

  // Check if there are any changes in the edit plan form
  const hasEditPlanChanges = () => {
    if (!plan) return false;

    return editPlanTitle !== plan.title || editPlanStartDate !== formatDateForInput(plan.start_date) || editPlanEndDate !== formatDateForInput(plan.end_date, -1);
  };

  // Handle updating the plan
  const handleUpdatePlan = () => {
    if (!plan || !editPlanTitle || !editPlanStartDate || !editPlanEndDate) return;

    const adjustedEnd = adjustEndDateForBackend(editPlanEndDate);

    // Check for overlapping plans
    const allPlans = plansData?.data || [];
    const overlappingPlans = findOverlappingPlans(allPlans, editPlanStartDate, adjustedEnd, plan.id);

    if (overlappingPlans.length > 0) {
      const overlappingPlanNames = overlappingPlans.map((plan) => plan.title).join(", ");
      setEditPlanValidationError(`This date range overlaps with an existing planned portal: ${overlappingPlanNames}`);
      return;
    }

    const formData = {
      organisation: userProfile.organisation_id,
      ...(isBrand
        ? {
            brand: brandId,
            ...(brandLocationCount > 0 ? { location: effectiveLocationId } : {}),
          }
        : { location: effectiveLocationId }),
      title: editPlanTitle,
      start_date: editPlanStartDate,
      end_date: adjustedEnd,
      is_main: false,
    };

    updatePlan(
      {
        planId: plan.id,
        formData,
      },
      {
        onSuccess: () => {
          setIsEditPlanPopoverOpen(false);
          setEditPlanTitle("");
          setEditPlanStartDate("");
          setEditPlanEndDate("");
          setEditPlanValidationError("");
          setToastMessage("Changes saved");
        },
        onError: (error) => {
          console.error("Error updating plan:", error);
          setEditPlanValidationError("Failed to update portal. Please try again.");
          setToastMessage("Failed to update plan");
        },
      }
    );
  };

  // Handle deleting the plan
  const handleDeletePlan = () => {
    if (!plan) return;

    deletePlan(
      {
        planId: plan.id,
        ...(isBrand
          ? {
              brand: brandId,
              ...(brandLocationCount > 0 ? { location: effectiveLocationId } : {}),
            }
          : { location: effectiveLocationId }),
      },
      {
        onSuccess: () => {
          setIsDeleteAlertOpen(false);
          setIsEditPlanPopoverOpen(false);
          setEditPlanTitle("");
          setEditPlanStartDate("");
          setEditPlanEndDate("");
          setEditPlanValidationError("");
          setToastMessage("Deleted");

          // Navigate back to planner
          if (isBrand) {
            navigate(`/portal-brand/${brandId}/planner`, {
              state: {
                isBrand: true,
                brandId: brandId,
                brandName: brandName,
                brandLocationCount: brandLocationCount,
              },
            });
          } else {
            navigate(`/portal-location/${paramId}/planner`, {
              state: {
                isBrand: false,
                locationName: locationName,
                locationAddress: locationAddress,
              },
            });
          }
        },
        onError: (error) => {
          console.error("Error deleting plan:", error);
          setIsDeleteAlertOpen(false);
          setToastMessage("Failed to delete plan");
        },
      }
    );
  };

  const getCheckedCount = () => {
    return [emailFlag, ageFlag, genderFlag, firstNameFlag, lastNameFlag, phoneNumberFlag].filter(Boolean).length;
  };

  const shouldDisableNames = !emailFlag && !phoneNumberFlag;
  const isDisabled = (current, nameField = false) => {
    if (nameField && shouldDisableNames) return true;
    return !current && getCheckedCount() >= 4;
  };

  useEffect(() => {
    if (isAirshipActive() && ageFlag) {
      setDobFlag(true);
    } else if (isAirshipActive() && !ageFlag) {
      setDobFlag(false);
    }
  }, [ageFlag, isAirshipActive]);

  useEffect(() => {
    const emailDisabled = !emailFlag;
    const phoneDisabled = !phoneNumberFlag;

    const shouldDisableNames = emailDisabled && phoneDisabled;

    if (shouldDisableNames) {
      setFirstNameFlag(false);
      setLastNameFlag(false);
    }
  }, [emailFlag, phoneNumberFlag]);


  return (
    <Flex direction="column" gap="5" p="5" style={{ alignSelf: "stretch", width: "100%", height: "100%" }}>
      {/* Navigation header with back button */}
      <Flex style={{ justifyContent: "space-between", alignItems: "center", alignSelf: "stretch" }}>
        <Flex gap="4" style={{ alignItems: "center", flex: "1 0 0" }}>
          <IconButton
            size="2"
            variant="soft"
            color="gray"
            onClick={() => {
              isBrand
                ? navigate(`/portal-brand/${brandId}/planner`, {
                    state: {
                      isBrand: true,
                      brandId: brandId,
                      brandName: brandName,
                      brandLocationCount: brandLocationCount,
                    },
                  })
                : navigate(`/portal-location/${paramId}/planner`, {
                    state: {
                      isBrand: false,
                      locationName: locationName,
                      locationAddress: locationAddress,
                    },
                  });
            }}
          >
            <ArrowLeftIcon width={16} height={16} />
          </IconButton>
          <Flex gap="1" direction="column" style={{ flex: "1 0 0", alignItems: "flex-start" }}>
            <Text size="2" weight="medium">
              {isBrand ? brandName : locationName}
            </Text>
            {isBrand && brandLocationCount > 0 ? (
              <HoverCard.Root>
                <HoverCard.Trigger>
                  <Text size="1" color="gray">
                    {brandLocationCount === 1 ? "1 location" : `${brandLocationCount} locations`}
                  </Text>
                </HoverCard.Trigger>
                <HoverCard.Content maxHeight="300px" style={{ padding: "var(--space-3)" }}>
                  <Flex direction="column" gap="3">
                    {locationsData
                      ?.filter((loc) => loc.brand_id === brandId)
                      .map((loc) => {
                        const live = liveAccessPointsStatus.find((l) => l.name === loc.name) || {};
                        const online = live.online_access_points_count ?? loc.online_access_points_count ?? 0;
                        const offline = live.offline_access_points_count ?? loc.offline_access_points_count ?? 0;
                        const statusColor = online > 0 && offline === 0 ? "var(--grass-9)" : online > 0 && offline > 0 ? "var(--amber-9)" : "var(--red-9)";
                        const isOnline = online > 0 && offline === 0;
                        const isPartial = online > 0 && offline > 0;
                        return (
                          <Flex key={loc.id} direction="column" gap="1">
                            <Flex align="center" gap="1">
                              <Tooltip content={isOnline ? "Online" : isPartial ? "Partially online" : "Offline"}>
                                <Box style={{ background: statusColor, width: "8px", height: "8px", borderRadius: "4px" }} />
                              </Tooltip>
                              <Text size="1" weight="medium">
                                {loc.name}
                              </Text>
                            </Flex>
                            <Text size="1" color="gray">
                              {loc.address || "No address"}
                            </Text>
                          </Flex>
                        );
                      })}
                  </Flex>
                </HoverCard.Content>
              </HoverCard.Root>
            ) : (
              <Text size="1" color="gray">
                {isBrand ? "0 locations" : locationAddress}
              </Text>
            )}
          </Flex>
        </Flex>
        {plan && (
          <Flex direction="column" gap="2" style={{ alignItems: "center", flex: "1 0 0" }}>
            {(() => {
              const { showGreenDot, badgeColor, badgeLabel, infoText } = getPlanBadgeInfo({
                plan,
                plansData,
                guestPortalSettings,
                formatDate,
                formatDateMinusOne,
              });
              return (
                <Flex direction="column" gap="2" style={{ alignItems: "center", flex: "1 0 0" }}>
                  <Flex gap="2" align="center">
                    {showGreenDot && <Box style={{ background: "var(--grass-9)", width: "8px", height: "8px", borderRadius: "4px" }} />}
                    <Badge color={badgeColor}>{badgeLabel}</Badge>
                  </Flex>
                  <Text size="1" color="gray" style={{ whiteSpace: "pre-line", textAlign: "center" }}>
                    {infoText}
                  </Text>
                </Flex>
              );
            })()}
          </Flex>
        )}
        <Flex gap="2" style={{ justifyContent: "flex-end", flex: "1 0 0", alignItems: "center" }}>
          <Tooltip content={`Switch to ${view === "mobile" ? "desktop" : "mobile"} view`} side="left">
            <IconButton size="2" variant="soft" color="gray" onClick={() => setView(view === "mobile" ? "desktop" : "mobile")}>
              {view === "mobile" ? <ComputerDesktopIcon width={16} height={16} /> : <DevicePhoneMobileIcon width={16} height={16} />}
            </IconButton>
          </Tooltip>
          {plan && !plan.is_main && (
            <EditPlanPopover
              isOpen={isEditPlanPopoverOpen}
              onOpenChange={setIsEditPlanPopoverOpen}
              editPlanTitle={editPlanTitle}
              setEditPlanTitle={setEditPlanTitle}
              editPlanStartDate={editPlanStartDate}
              setEditPlanStartDate={setEditPlanStartDate}
              editPlanEndDate={editPlanEndDate}
              setEditPlanEndDate={setEditPlanEndDate}
              onUpdatePlan={handleUpdatePlan}
              isUpdating={isUpdatingPlan}
              validationError={editPlanValidationError}
              setValidationError={setEditPlanValidationError}
              onDeletePlan={handleDeletePlan}
              hasChanges={hasEditPlanChanges()}
              isDeleteAlertOpen={isDeleteAlertOpen}
              setIsDeleteAlertOpen={setIsDeleteAlertOpen}
              isDeletingPlan={isDeletingPlan}
              isActivePlan={isCurrentPlanActive()}
            >
              <IconButton size="2" variant="soft" color="gray" onClick={handleEditPlanClick}>
                <DotsHorizontalIcon width={16} height={16} />
              </IconButton>
            </EditPlanPopover>
          )}
        </Flex>
      </Flex>

      <React.Fragment>
        <Flex gap="5" style={{ alignSelf: "stretch", width: "100%", height: "100%", alignItems: "center", flex: "1 0 0" }}>
          {/* Settings Section */}
          <Flex style={{ alignItems: "flex-start", alignSelf: "stretch" }} height="100%">
            <Form.Root onSubmit={handleSubmit} style={{ height: "100%", width: "100%" }}>
              <Flex
                justify="between"
                maxWidth="400px"
                direction="column"
                style={{
                  alignItems: "flex-start",
                  alignSelf: "stretch",
                  flex: "1 0 0",
                  borderRadius: "var(--radius-4)",
                  height: "100%",
                  border: "1px solid var(--gray-a6)",
                  backgroundColor: "var(--color-panel-solid)",
                }}
              >
                <Flex direction="column" style={{ alignSelf: "stretch", overflow: "scroll", flex: "1 0 0" }}>
                  {/* Settings Header */}
                  <Tabs.Root
                    defaultValue="portal"
                    value={activeTab}
                    onValueChange={(value) => {
                      setActiveTab(value);
                      // Active tab is persisted via state
                    }}
                  >
                    <Tabs.List
                      style={{
                        padding: "var(--space-2) var(--space-4) 0px var(--space-4)",
                        position: "sticky",
                        top: "0",
                        zIndex: "1",
                        backgroundColor: "var(--color-panel-solid)",
                        borderTopLeftRadius: "var(--radius-4)",
                        borderTopRightRadius: "var(--radius-4)",
                      }}
                    >
                      <Tabs.Trigger value="portal">Form</Tabs.Trigger>
                      {emailFlag && <Tabs.Trigger value="policies">Privacy</Tabs.Trigger>}
                      <Tabs.Trigger value="landing">Landing</Tabs.Trigger>
                    </Tabs.List>
                    {/* Portal Tab */}
                    <Tabs.Content value="portal" key="portal-tab">
                      <Flex p="4" gap="2" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                        <Text size="1" weight="regular" color="gray">
                          Your portal will appear on your guests' device when they connect to your guest Wi-Fi for the first time.
                        </Text>
                      </Flex>
                      {/* Email, Age, Gender, First Name, and Last Name Selects */}
                      <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                      <Flex p="4" gap="2" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                        <Flex
                            style={{
                              justifyContent: "space-between",
                              alignSelf: "stretch",
                              alignItems: "center",
                              width: "100%",
                            }}
                        >
                          <Text size="2" weight="medium">Data Fields</Text>
                          <Text size="1" weight="regular" color="gray">Maximum 4 fields</Text>
                        </Flex>
                        <CheckboxGroup.Root
                          style={{ width: "100%", gap: "var(--space-2)" }}
                          value={[emailFlag ? "email" : "", ageFlag ? "age" : "", genderFlag ? "gender" : "", firstNameFlag ? "first-name" : "", lastNameFlag ? "last-name" : "", phoneNumberFlag ? "phone-number" : "",]}
                          onValueChange={handleCpFormFieldsChange}
                        >
                          {/* First Name */}
                          <Flex
                            p="2"
                            style={{ justifyContent: "flex-start", alignSelf: "stretch", alignItems: "center", width: "100%", borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)" }}
                          >
                            {/* <CheckboxGroup.Item value="first-name" disabled={isDisabled(firstNameFlag)}>First name</CheckboxGroup.Item> */}
                            {shouldDisableNames ? (
                              <Tooltip content="You must enable email address or phone number first.">
                                <Box>
                                  <CheckboxGroup.Item value="first-name" disabled>First Name</CheckboxGroup.Item>
                                </Box>
                              </Tooltip>
                            ) : (
                              <CheckboxGroup.Item value="first-name" disabled={isDisabled(firstNameFlag, true)}>First name</CheckboxGroup.Item>
                            )}
                          </Flex>
                          {/* Last Name */}
                          <Flex
                            p="2"
                            style={{ justifyContent: "flex-start", alignSelf: "stretch", alignItems: "center", width: "100%", borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)" }}
                          >
                            {/* <CheckboxGroup.Item value="last-name" disabled={isDisabled(lastNameFlag)}>Last name</CheckboxGroup.Item> */}
                            {shouldDisableNames ? (
                              <Tooltip content="You must enable email address or phone number first.">
                                <Box>
                                  <CheckboxGroup.Item value="last-name" disabled>Last Name</CheckboxGroup.Item>
                                </Box>
                              </Tooltip>
                            ) : (
                              <CheckboxGroup.Item value="last-name" disabled={isDisabled(lastNameFlag, true)}>Last name</CheckboxGroup.Item>
                            )}
                          </Flex>
                          <Flex
                            p="2"
                            style={{
                              justifyContent: "space-between",
                              alignSelf: "stretch",
                              alignItems: "center",
                              width: "100%",
                              borderRadius: "var(--radius-2)",
                              border: "1px solid var(--gray-a6)",
                            }}
                          >
                            <CheckboxGroup.Item value="email" disabled={isDisabled(emailFlag)}>Email</CheckboxGroup.Item>
                            <Tooltip width="210px" content="Make sure to select your privacy notice method in the privacy tab." style={{ textAlign: "center" }}>
                              <ExclamationTriangleIcon color="gray" />
                            </Tooltip>
                          </Flex>
                          {/* Age */}
                          <Flex
                            p="2"
                            style={{
                              justifyContent: "space-between",
                              alignSelf: "stretch",
                              alignItems: "center",
                              width: "100%",
                              borderRadius: "var(--radius-2)",
                              border: "1px solid var(--gray-a6)",
                            }}
                          >
                            <CheckboxGroup.Item value="age" disabled={isDisabled(ageFlag)}>Age</CheckboxGroup.Item>
                            <Text as="label" size="1" color="gray">
                              <Flex gap="2">
                                {isAirshipActive() ? (
                                  <Tooltip width="210px" content="Your Airship integration is compatible only with Date of birth." style={{ textAlign: "center" }}>
                                    <Flex style={{ display: "inline-flex" }}>
                                      {/* I believe the next line requires that you are checked if ageFlag is also checked. This means that AirShip will always 
                                      have dob when age is selected, but dob flag won't be selected when age is not selected, to avoid errors where we have dob selected when age is not selected.
                                      The idea is to have the dob flag mirror the age flag whenever Airship is Active */}
                                      <Checkbox name="dob" value="dob" disabled={!ageFlag || isAirshipActive()} checked={ageFlag && dobFlag} /> 
                                    </Flex>
                                  </Tooltip>
                                ) : (
                                  <Flex style={{ display: "inline-flex" }}>
                                    <Checkbox name="dob" value="dob" disabled={!ageFlag} checked={dobFlag && ageFlag} onCheckedChange={() => setDobFlag(!dobFlag)} />
                                  </Flex>
                                )}
                                Date of birth
                              </Flex>
                            </Text>
                          </Flex>
                          {/* Gender */}
                          <Flex
                            p="2"
                            style={{ justifyContent: "flex-start", alignSelf: "stretch", alignItems: "center", width: "100%", borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)" }}
                          >
                            <CheckboxGroup.Item value="gender" disabled={isDisabled(genderFlag)}>Gender</CheckboxGroup.Item>
                          </Flex>
                          {/* Phone Number */}
                          <Flex
                            p="2"
                            style={{ justifyContent: "flex-start", alignSelf: "stretch", alignItems: "center", width: "100%", borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)" }}
                          >
                            <CheckboxGroup.Item value="phone-number" disabled={isDisabled(phoneNumberFlag)}>Phone Number</CheckboxGroup.Item>
                          </Flex>
                        </CheckboxGroup.Root>
                      </Flex>
                      {/* Alternative Login Method Field */}
                      {userProfile?.add_facebook_button_state ? (
                        <React.Fragment>
                          <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                          <Flex style={{ alignSelf: "stretch" }} gap="4" p="4" direction="column" align="start">
                            <Text size="2" weight="medium">
                              Alternative log in methods
                            </Text>
                            <CheckboxGroup.Root
                              value={[emailFlag ? "email" : "", ageFlag ? "age" : "", genderFlag ? "gender" : "", firstNameFlag ? 'first-name': '', lastNameFlag ? 'last-name': '', phoneNumberFlag ? 'phone-number': '', facebookFlag ? "facebook" : ""]}
                              onValueChange={handleCpFormFieldsChange}
                              style={{ width: "100%", gap: "var(--space-2)" }}
                            >
                              <Flex p="2" align="center" justify="start" style={{ alignSelf: "stretch", width: "100%", borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)" }}>
                                <CheckboxGroup.Item value="facebook">Facebook</CheckboxGroup.Item>
                              </Flex>
                            </CheckboxGroup.Root>
                          </Flex>
                        </React.Fragment>
                      ) : null}
                      {/* Logo Input Field */}
                      <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                      <Flex gap="1" p="4" direction="column" style={{ alignSelf: "stretch" }}>
                        <Form.Field name="logo_image">
                          <Form.Label style={{ display: "flex", flexDirection: "column", marginBottom: "4px" }}>
                            <Text size="2" weight="medium">
                              Logo
                            </Text>
                          </Form.Label>
                          <Flex gap="2">
                            <input type="file" style={{ display: "none" }} ref={logoInputRef} onChange={handleLogoUpload} accept="image/*" />
                            <Button
                              type="button"
                              size="2"
                              variant="surface"
                              color="gray"
                              style={{ display: "flex", padding: "0px var(--space-2)", justifyContent: "center", flex: "1" }}
                              onClick={triggerLogoInput}
                            >
                              {logoPreview ? (
                                <Tooltip content="Upload new to replace">
                                  <Text>{logoFileName?.length >= 18 ? `${logoFileName.slice(0, 18)}...` : logoFileName}</Text>
                                </Tooltip>
                              ) : (
                                <>
                                  <UploadIcon />
                                  <Text>Upload</Text>
                                </>
                              )}
                            </Button>
                            {logoPreview && (
                              <Button type="button" size="2" variant="surface" color="gray" onClick={() => handleLogoClear()}>
                                Clear
                              </Button>
                            )}
                          </Flex>
                        </Form.Field>
                        <Text size="1" weight="regular" color="gray">
                          Recommended size of 200px by 96px. Maximum file size of 1MB.
                        </Text>
                      </Flex>
                      <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                      {/* Background Image & Background Overlay */}
                      <Flex direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                        <Flex gap="4" p="4" direction="column" style={{ alignSelf: "stretch" }}>
                          {/* Background Image */}
                          <Flex gap="1" direction="column" style={{ alignSelf: "stretch" }}>
                            <Form.Field name="background_image">
                              <Form.Label style={{ display: "flex", flexDirection: "column", marginBottom: "4px" }}>
                                <Text size="2" weight="medium">
                                  Background image
                                </Text>
                              </Form.Label>
                              <Flex gap="2" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                                <input
                                  type="file"
                                  style={{ display: "none" }}
                                  ref={backgroundInputRef}
                                  onChange={handleBackgroundImageUpload}
                                  id="background-upload"
                                  accept="image/*"
                                  name="background_image"
                                />
                                <Button
                                  type="button"
                                  size="2"
                                  variant="surface"
                                  color="gray"
                                  style={{ display: "flex", padding: "0px var(--space-2)", justifyContent: "center", flex: "1" }}
                                  onClick={triggerBackgroundInput}
                                >
                                  {backgroundPreview ? (
                                    <Tooltip content="Upload new to replace">
                                      <Text>{backgroundFileName?.length >= 18 ? `${backgroundFileName.slice(0, 18)}...` : backgroundFileName}</Text>
                                    </Tooltip>
                                  ) : (
                                    <>
                                      <UploadIcon />
                                      <Text>Upload</Text>
                                    </>
                                  )}
                                </Button>
                                {backgroundPreview && (
                                  <Button type="button" size="2" variant="surface" color="gray" onClick={() => handleBackgroundImageClear()}>
                                    Clear
                                  </Button>
                                )}
                              </Flex>
                            </Form.Field>
                            <Text size="1" weight="regular" color="gray">
                              Recommended size of 900px by 600px. Maximum file size of 1MB.
                            </Text>
                          </Flex>
                          {/* Background Overlay */}
                          <Flex gap="1" direction="column" style={{ alignSelf: "stretch" }}>
                            <Form.Field name="background_overlay">
                              <Form.Label style={{ display: "flex", flexDirection: "column", marginBottom: "4px" }}>
                                <Text size="2" weight="medium">
                                  Background overlay
                                </Text>
                              </Form.Label>
                              <Form.Control asChild>
                                <input type="hidden" name="background_overlay" value={backgroundOverlayColor} />
                              </Form.Control>
                              <Flex gap="2" style={{ alignSelf: "stretch", alignItems: "flex-start" }}>
                                <label htmlFor="background_overlay_colour_picker" style={{ position: "relative", display: "inline-block", width: "100%" }}>
                                  <input
                                    type="color"
                                    id="background_overlay_colour_picker"
                                    value={backgroundOverlayColor}
                                    onChange={(e) => {
                                      setBackgroundOverlayColor(e.target.value);
                                      setIsDirty(true); // Mark form as dirty on change
                                    }}
                                    style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0 }}
                                  />
                                  {/* Custom color display */}
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "32px",
                                      display: "flex",
                                      backgroundColor: colorConversion(backgroundOverlayColor, backgroundOverlayOpacity),
                                      borderRadius: "var(--radius-2)",
                                      border: "1px solid var(--gray-a6)",
                                    }}
                                  ></div>
                                </label>
                                <TextField.Root
                                  size="2"
                                  type="number"
                                  min={0}
                                  max={100}
                                  step={1}
                                  value={backgroundOverlayOpacity}
                                  onChange={(e) => {
                                    setBackgroundOverlayOpacity(e.target.value);
                                  }}
                                  style={{ textAlign: "center", width: "70px" }}
                                >
                                  <TextField.Slot side="right" style={{ padding: "4px" }}>
                                    %
                                  </TextField.Slot>
                                </TextField.Root>
                              </Flex>
                              <Text size="1" weight="regular" color="gray">
                                Recommended for logo visibility.
                              </Text>
                            </Form.Field>
                          </Flex>
                        </Flex>
                        <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                        {/* Button Colour Input Field */}
                        <Flex gap="1" p="4" direction="column" style={{ alignSelf: "stretch" }}>
                          <Form.Field name="button_color">
                            <Form.Label style={{ display: "flex", flexDirection: "column", marginBottom: "4px" }}>
                              <Text size="2" weight="medium">
                                Button colour
                              </Text>
                            </Form.Label>
                            <Form.Control asChild>
                              <input type="hidden" name="button_color" value={buttonColor} />
                            </Form.Control>
                            <Flex direction="column" style={{ alignSelf: "stretch" }}>
                              <label htmlFor="button_colour_picker" style={{ position: "relative", display: "inline-block", width: "100%" }}>
                                <input
                                  type="color"
                                  id="button_colour_picker"
                                  value={buttonColor}
                                  onChange={(e) => {
                                    setButtonColor(e.target.value);
                                    setIsDirty(true); // Mark form as dirty on change
                                  }}
                                  style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0 }}
                                />
                                <div style={{ width: "100%", height: "32px", backgroundColor: buttonColor, borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)" }}></div>
                              </label>
                            </Flex>
                          </Form.Field>
                          <Text size="1" weight="regular" color="gray">
                            Recommended contrast of 3:1, against white. Visit &nbsp;
                            <Link color="blue" underline="always" target="_blank" href="https://webaim.org/resources/contrastchecker/">
                              WebAim's Contrast Checker
                            </Link>
                            &nbsp;to test your button colour.
                          </Text>
                        </Flex>
                      </Flex>
                      {/* Custom Message */}
                      <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                      <Flex p="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                        <Text size="2" weight="medium">
                          Message
                        </Text>
                        <TextField.Root size="2" value={message} onChange={(e) => handleInputMessageChange(e.target.value)} maxLength={48} style={{ alignSelf: "stretch" }} />
                      </Flex>
                    </Tabs.Content>
                    {/* Privacy Policies Tab */}
                    <Tabs.Content value="policies" key="policies-tab">
                      <Flex p="4" direction="column" gap="4" style={{ alignSelf: "stretch", flex: "1 0 0" }}>
                        <RadioGroup.Root
                          value={privacyType}
                          onValueChange={(value) => {
                            setPrivacyType(value);
                            setIsDirty(true); // Mark form as dirty immediately
                          }}
                          style={{ width: "100%" }}
                        >
                          <Flex direction="row" gap="2" style={{ width: "100%" }}>
                            <Text as="label" size="2" style={{ width: "100%" }}>
                              <Flex p="2" gap="2" style={{ alignItems: "center", flex: "1 0 0", border: "1px solid var(--gray-a6)", borderRadius: "var(--radius-2)" }}>
                                <RadioGroup.Item value="auto" />
                                Auto
                              </Flex>
                            </Text>
                            <Text as="label" size="2" style={{ width: "100%" }}>
                              <Flex p="2" gap="2" style={{ alignItems: "center", flex: "1 0 0", border: "1px solid var(--gray-a6)", borderRadius: "var(--radius-2)" }}>
                                <RadioGroup.Item value="custom" />
                                Custom
                              </Flex>
                            </Text>
                          </Flex>
                        </RadioGroup.Root>
                        {privacyType === "auto" ? (
                          <Flex direction="column" gap="4" style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0" }}>
                            <Text size="1" weight="regular" color="gray">
                              Your privacy notice has been created using your legal information and will be displayed on your captive portal.
                            </Text>
                            <Button size="2" variant="soft" color="gray" style={{ alignSelf: "stretch" }} onClick={() => navigate("/organisation/details")}>
                              Edit legal details
                              <ArrowTopRightOnSquareIcon width="16" height="16" />
                            </Button>
                          </Flex>
                        ) : (
                          <Flex direction="column" gap="4" style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0" }}>
                            <Text size="1" color="gray">
                              Provide a custom privacy notice below. You can style it using&nbsp;
                              <Link color="gray" underline="always" href="https://www.markdownguide.org/cheat-sheet/">
                                markdown
                              </Link>
                              .
                            </Text>
                            <Flex direction="column" gap="1" style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0" }}>
                              <Text size="2" weight="medium" style={{ width: "100%" }}>
                                Privacy Profile
                              </Text>
                              <TextArea
                                resize="vertical"
                                size="2"
                                style={{ width: "100%" }}
                                rows="13"
                                value={privacyPolicy}
                                onChange={(e) => handlePrivacyPolicyChange(e.target.value)}
                                placeholder="Paste here"
                              />
                              <Text size="1" color="gray">
                                Auto will be used as a fallback if no privacy notice is provided.
                              </Text>
                            </Flex>
                          </Flex>
                        )}
                      </Flex>
                    </Tabs.Content>
                    {/* Landing Tab */}
                    <Tabs.Content value="landing" key="landing-tab">
                      {/* Page / Redirect */}
                      <Flex p="4" direction="column" gap="4" style={{ alignItems: "center", alignSelf: "stretch", flex: "1 0 0" }}>
                        <RadioGroup.Root
                          value={segmentView}
                          onValueChange={(value) => {
                            setSegmentView(value);
                            setLandingType(value); // Updating landing type when switching tabs
                            setIsDirty(true); // Mark form as dirty immediately
                          }}
                          style={{ width: "100%" }}
                        >
                          <Flex direction="row" gap="2" style={{ width: "100%" }}>
                            <Text as="label" size="2" style={{ width: "100%" }}>
                              <Flex p="2" gap="2" style={{ alignItems: "center", flex: "1 0 0", border: "1px solid var(--gray-a6)", borderRadius: "var(--radius-2)" }}>
                                <RadioGroup.Item value="page" />
                                Page
                              </Flex>
                            </Text>
                            <Text as="label" size="2" style={{ width: "100%" }}>
                              <Flex p="2" gap="2" style={{ alignItems: "center", flex: "1 0 0", border: "1px solid var(--gray-a6)", borderRadius: "var(--radius-2)" }}>
                                <Tooltip
                                  width="300px"
                                  content="Redirection is temporarily disabled while b connect 
                                  resolves connectivity issues related to this feature."
                                >
                                  <RadioGroup.Item value="redirect" disabled />
                                </Tooltip>
                                Redirect
                              </Flex>
                            </Text>
                          </Flex>
                        </RadioGroup.Root>
                        <Flex gap="2" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                          <Text size="1" weight="regular" color="gray">
                            Your landing page will appear after a guest has been connected to the internet.
                          </Text>
                          <Tooltip
                            content="This applies mostly to iOS, MacOS & Windows devices. 
                                            Most Android devices auto-close the portal upon 
                                            connection."
                          >
                            <InfoCircledIcon color="gray" width="24" height="24" />
                          </Tooltip>
                        </Flex>
                      </Flex>
                      <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                      {/* Page Tab */}
                      {segmentView === "page" ? (
                        <Flex direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0" }}>
                          {/* Link Section under Page Tab */}
                          <Flex p="4" gap="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0" }}>
                            <Text as="label" size="2" style={{ width: "100%" }}>
                              <Flex gap="2" style={{ alignItems: "center", alignSelf: "stretch" }}>
                                <Checkbox
                                  checked={showLinks}
                                  onCheckedChange={(e) => {
                                    setShowLinks(e);
                                    setIsDirty(true); // Mark form as dirty immediately
                                  }}
                                />
                                <Text size="3" weight="medium">
                                  Links
                                </Text>
                              </Flex>
                            </Text>
                            {showLinks && (
                              <Flex direction="column" gap="2" style={{ width: "100%" }}>
                                {/* Rendering existing links */}
                                {links.map((link, index) => (
                                  <Popover.Root
                                    key={index}
                                    open={currentEditingLink && currentEditingLink.index === index}
                                    onOpenChange={(open) => {
                                      if (!open) {
                                        if (currentEditingLink && currentEditingLink.index === index && !linkIconUploadFailed) {
                                          saveLinkChanges();
                                        } else if (linkIconUploadFailed) {
                                          closeLinkEditor();
                                          setLinkIconUploadFailed(false);
                                        }
                                      }
                                    }}
                                  >
                                    <Popover.Trigger asChild>
                                      <Button
                                        type="button"
                                        size="2"
                                        variant="surface"
                                        color={currentEditingLink && currentEditingLink.index === index ? "blue" : "gray"}
                                        style={{ width: "100%", alignSelf: "stretch", justifyContent: "center" }}
                                        onClick={() => openLinkEditor(link, index)}
                                      >
                                        {link.label || `Link ${index + 1}`}
                                      </Button>
                                    </Popover.Trigger>
                                    <Popover.Content side="left">
                                      <Flex direction="column" gap="4">
                                        <Flex style={{ alignSelf: "stretch", justifyContent: "space-between", alignItems: "center" }}>
                                          <Text size="3">Link settings</Text>
                                          <Popover.Close>
                                            <IconButton size="1" variant="ghost" color="gray">
                                              <Cross1Icon />
                                            </IconButton>
                                          </Popover.Close>
                                        </Flex>

                                        {/* Link form fields */}
                                        <Flex direction="column" gap="4">
                                          <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                                            <Flex gap="1" direction="column">
                                              <Text size="2" weight="medium">
                                                Label
                                              </Text>
                                              <TextField.Root size="2" value={currentEditingLink?.label || ""} onChange={(e) => updateLinkField("label", e.target.value)} />
                                            </Flex>
                                          </Text>

                                          <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                                            <Flex gap="1" direction="column">
                                              <Text size="2" weight="medium">
                                                Icon
                                              </Text>
                                              <Flex gap="2">
                                                <input
                                                  type="file"
                                                  style={{ display: "none" }}
                                                  ref={linkIconUpload.inputRef}
                                                  onChange={(e) => {
                                                    const result = linkIconUpload.handleUpload(e);
                                                    if (!result.success) {
                                                      if (result.reason === "size-exceeded") {
                                                        setToastMessage("Maximum file size of 1MB");
                                                        setLinkIconUploadFailed(true);
                                                      }
                                                    } else {
                                                      setToastMessage("Uploaded");
                                                      setLinkIconUploadFailed(false);
                                                    }
                                                  }}
                                                  accept="image/*"
                                                />
                                                <Button
                                                  type="button"
                                                  size="2"
                                                  variant="surface"
                                                  color="gray"
                                                  style={{ display: "flex", padding: "0px var(--space-2)", justifyContent: "center", flex: "1" }}
                                                  onClick={linkIconUpload.triggerInput}
                                                >
                                                  {linkIconUpload.preview ? (
                                                    <Tooltip content="Upload new to replace">
                                                      <Text>{linkIconUpload.fileName?.length >= 18 ? `${linkIconUpload.fileName.slice(0, 18)}...` : linkIconUpload.fileName}</Text>
                                                    </Tooltip>
                                                  ) : (
                                                    <>
                                                      <UploadIcon />
                                                      <Text>Upload</Text>
                                                    </>
                                                  )}
                                                </Button>

                                                {linkIconUpload.preview && (
                                                  <Button
                                                    type="button"
                                                    size="2"
                                                    variant="surface"
                                                    color="gray"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      linkIconUpload.handleClear();
                                                    }}
                                                  >
                                                    Clear
                                                  </Button>
                                                )}
                                              </Flex>
                                              <Text size="1" weight="regular" color="gray">
                                                Recommended size of 40px by 40px. Maximum file size of 1MB.
                                              </Text>
                                            </Flex>
                                          </Text>

                                          <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                                            <Flex gap="2" direction="column">
                                              <Flex gap="1" direction="column">
                                                <Text size="2" weight="medium">
                                                  Link URL
                                                </Text>
                                                {!(currentEditingLink?.unique_url_flag && canHaveUniqueUrls) && (
                                                  <TextField.Root size="2" value={currentEditingLink?.link_url || ""} onChange={(e) => updateLinkField("link_url", e.target.value)} />
                                                )}
                                              </Flex>
                                              {canHaveUniqueUrls && (
                                                <Text as="label" size="1">
                                                  <Flex gap="2">
                                                    <Checkbox checked={currentEditingLink?.unique_url_flag} onCheckedChange={(val) => updateLinkField("unique_url_flag", val)} />
                                                    Unique URL per location
                                                  </Flex>
                                                </Text>
                                              )}
                                            </Flex>
                                          </Text>
                                          {currentEditingLink?.unique_url_flag && canHaveUniqueUrls && (
                                            <Flex
                                              p="2"
                                              direction="column"
                                              gap="2"
                                              style={{
                                                maxHeight: "120px",
                                                alignItems: "flex-start",
                                                alignSelf: "stretch",
                                                overflow: "auto",
                                                border: "1px solid var(--gray-a6)",
                                                borderRadius: "var(--radius-2)",
                                                backgroundColor: "var(--color-panel-translucent)",
                                              }}
                                            >
                                              {brandLocations.map((loc) => (
                                                <Flex gap="1" direction="column" style={{ alignSelf: "stretch" }} key={loc.id}>
                                                  <Text size="2" weight="medium">
                                                    {loc.name}
                                                  </Text>
                                                  <TextField.Root
                                                    key={loc.id}
                                                    size="2"
                                                    value={locationLinkUrls[currentEditingLink.id]?.[loc.id] || ""}
                                                    onChange={(e) =>
                                                      setLocationLinkUrls((prev) => ({
                                                        ...prev,
                                                        [currentEditingLink.id]: {
                                                          ...prev[currentEditingLink.id],
                                                          [loc.id]: e.target.value,
                                                        },
                                                      }))
                                                    }
                                                  />
                                                </Flex>
                                              ))}
                                            </Flex>
                                          )}
                                        </Flex>

                                        <Flex style={{ alignSelf: "stretch", marginTop: "var(--space-2)" }}>
                                          <Button type="button" size="2" variant="soft" color="gray" onClick={() => handleLinkRemove(currentEditingLink.index)} style={{ width: "100%" }}>
                                            Remove link
                                          </Button>
                                        </Flex>
                                      </Flex>
                                    </Popover.Content>
                                  </Popover.Root>
                                ))}

                                {/* Add new link button with its own popover */}
                                {links.length < 3 && (
                                  <Popover.Root
                                    open={isAddingLink}
                                    onOpenChange={(open) => {
                                      if (!open) {
                                        // If popover is closing, check if any field has a value
                                        const hasContent =
                                          (currentEditingLink?.label && currentEditingLink.label.trim() !== "") ||
                                          (currentEditingLink?.link_url && currentEditingLink.link_url.trim() !== "") ||
                                          linkIconUpload.preview;

                                        if (hasContent) {
                                          // At least one field has content, save the link
                                          saveLinkChanges();
                                        } else {
                                          // No content, just close without saving
                                          closeLinkEditor();
                                        }
                                      }
                                    }}
                                  >
                                    <Popover.Trigger asChild>
                                      <Button
                                        size="2"
                                        variant="soft"
                                        color="gray"
                                        style={{ width: "100%", alignSelf: "stretch", gap: "var(--space-2)", justifyContent: "center" }}
                                        onClick={() => openLinkEditor(null)}
                                      >
                                        <PlusIcon />
                                        Add link
                                      </Button>
                                    </Popover.Trigger>
                                    <Popover.Content side="left">
                                      <Flex direction="column" gap="4">
                                        <Flex style={{ alignSelf: "stretch", justifyContent: "space-between", alignItems: "center" }}>
                                          <Text size="3">Link settings</Text>
                                          <Popover.Close>
                                            <IconButton size="1" variant="ghost" color="gray">
                                              <Cross1Icon />
                                            </IconButton>
                                          </Popover.Close>
                                        </Flex>

                                        {/* Link form fields */}
                                        <Flex direction="column" gap="4">
                                          <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                                            <Flex gap="1" direction="column">
                                              <Text size="2" weight="medium">
                                                Label
                                              </Text>
                                              <TextField.Root size="2" value={currentEditingLink?.label || ""} onChange={(e) => updateLinkField("label", e.target.value)} />
                                            </Flex>
                                          </Text>

                                          <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                                            <Flex gap="1" direction="column">
                                              <Text size="2" weight="medium">
                                                Icon
                                              </Text>
                                              <Flex gap="2">
                                                <input
                                                  type="file"
                                                  style={{ display: "none" }}
                                                  ref={linkIconUpload.inputRef}
                                                  onChange={(e) => {
                                                    const result = linkIconUpload.handleUpload(e);
                                                    if (!result.success) {
                                                      if (result.reason === "size-exceeded") {
                                                        setToastMessage("Maximum file size of 1MB");
                                                      }
                                                    } else {
                                                      setToastMessage("Uploaded");
                                                    }
                                                  }}
                                                  accept="image/*"
                                                />
                                                <Button
                                                  type="button"
                                                  size="2"
                                                  variant="surface"
                                                  color="gray"
                                                  style={{ display: "flex", padding: "0px var(--space-2)", justifyContent: "center", flex: "1" }}
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    linkIconUpload.triggerInput();
                                                  }}
                                                >
                                                  {linkIconUpload.preview ? (
                                                    <Tooltip content="Upload new to replace">
                                                      <Text>{linkIconUpload.fileName?.length >= 18 ? `${linkIconUpload.fileName.slice(0, 18)}...` : linkIconUpload.fileName}</Text>
                                                    </Tooltip>
                                                  ) : (
                                                    <>
                                                      <UploadIcon />
                                                      <Text>Upload</Text>
                                                    </>
                                                  )}
                                                </Button>

                                                {linkIconUpload.preview && (
                                                  <Button
                                                    type="button"
                                                    size="2"
                                                    variant="surface"
                                                    color="gray"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      linkIconUpload.handleClear();
                                                    }}
                                                  >
                                                    Clear
                                                  </Button>
                                                )}
                                              </Flex>
                                              <Text size="1" weight="regular" color="gray">
                                                Recommended size of 40px by 40px. Maximum file size of 1MB.
                                              </Text>
                                            </Flex>
                                          </Text>

                                          <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                                            <Flex gap="2" direction="column">
                                              <Flex gap="1" direction="column">
                                                <Text size="2" weight="medium">
                                                  Link URL
                                                </Text>
                                                {!(currentEditingLink?.unique_url_flag && canHaveUniqueUrls) && (
                                                  <TextField.Root size="2" value={currentEditingLink?.link_url || ""} onChange={(e) => updateLinkField("link_url", e.target.value)} />
                                                )}
                                              </Flex>
                                              {canHaveUniqueUrls && (
                                                <Text as="label" size="1">
                                                  <Flex gap="2">
                                                    <Checkbox checked={currentEditingLink?.unique_url_flag} onCheckedChange={(val) => updateLinkField("unique_url_flag", val)} />
                                                    Unique URL per location
                                                  </Flex>
                                                </Text>
                                              )}
                                            </Flex>
                                          </Text>
                                          {currentEditingLink?.unique_url_flag && canHaveUniqueUrls && (
                                            <Flex
                                              p="2"
                                              direction="column"
                                              gap="2"
                                              style={{
                                                maxHeight: "120px",
                                                alignItems: "flex-start",
                                                alignSelf: "stretch",
                                                overflow: "auto",
                                                border: "1px solid var(--gray-a6)",
                                                borderRadius: "var(--radius-2)",
                                                backgroundColor: "var(--color-panel-translucent)",
                                              }}
                                            >
                                              {brandLocations.map((loc) => (
                                                <Flex gap="1" direction="column" style={{ alignSelf: "stretch" }} key={loc.id}>
                                                  <Text size="2" weight="medium">
                                                    {loc.name}
                                                  </Text>
                                                  <TextField.Root
                                                    key={loc.id}
                                                    size="2"
                                                    value={locationLinkUrls[currentEditingLink.id]?.[loc.id] || ""}
                                                    onChange={(e) =>
                                                      setLocationLinkUrls((prev) => ({
                                                        ...prev,
                                                        [currentEditingLink.id]: {
                                                          ...prev[currentEditingLink.id],
                                                          [loc.id]: e.target.value,
                                                        },
                                                      }))
                                                    }
                                                  />
                                                </Flex>
                                              ))}
                                            </Flex>
                                          )}
                                        </Flex>
                                      </Flex>
                                    </Popover.Content>
                                  </Popover.Root>
                                )}
                              </Flex>
                            )}
                          </Flex>
                          <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                          {/* Banner section under Page Tab */}
                          <Flex p="4" gap="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0" }}>
                            <Text as="label" size="2">
                              <Flex gap="2" style={{ alignItems: "center", alignSelf: "stretch" }}>
                                <Checkbox
                                  checked={showBanner}
                                  onCheckedChange={(e) => {
                                    setShowBanner(e);
                                    setIsDirty(true); // Mark form as dirty immediately
                                  }}
                                />
                                <Text size="3" weight="medium">
                                  Banner
                                </Text>
                              </Flex>
                            </Text>
                            {showBanner && (
                              <Flex gap="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0" }}>
                                {/* Banner title input field */}
                                <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                                  <Flex gap="1" direction="column">
                                    <Text size="2" weight="medium">
                                      Title
                                    </Text>
                                    <TextField.Root
                                      size="2"
                                      value={bannerTitle}
                                      onChange={(e) => {
                                        setBannerTitle(e.target.value);
                                        setIsDirty(true); // Mark form as dirty immediately
                                      }}
                                    />
                                  </Flex>
                                </Text>
                                {/* Banner description input field */}
                                <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                                  <Flex gap="1" direction="column">
                                    <Text size="2" weight="medium">
                                      Description
                                    </Text>
                                    <TextField.Root
                                      size="2"
                                      value={bannerDescription}
                                      onChange={(e) => {
                                        setBannerDescription(e.target.value);
                                        setIsDirty(true); // Mark form as dirty immediately
                                      }}
                                    />
                                    <Text size="1" weight="regular" color="gray">
                                      Recommended length of 100 characters.
                                    </Text>
                                  </Flex>
                                </Text>
                                {/* Banner URL field */}
                                <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                                  <Flex gap="1" direction="column">
                                    <Text size="2" weight="medium">
                                      Link URL
                                    </Text>
                                    <TextField.Root
                                      size="2"
                                      value={bannerLinkURL}
                                      onChange={(e) => {
                                        setBannerLinkURL(e.target.value);
                                        setIsDirty(true); // Mark form as dirty immediately
                                      }}
                                    />
                                  </Flex>
                                </Text>
                                {/* Banner Image upload field */}
                                <Text as="label" size="2" style={{ alignSelf: "stretch" }}>
                                  <Flex gap="1" direction="column">
                                    <Text size="2" weight="medium">
                                      Image
                                    </Text>
                                    <Flex gap="2">
                                      <input
                                        type="file"
                                        accept="image/*"
                                        id="banner-upload"
                                        name="banner_image"
                                        style={{ display: "none" }}
                                        ref={bannerImage.inputRef}
                                        onChange={(e) => {
                                          const result = bannerImage.handleUpload(e);
                                          if (!result.success) {
                                            if (result.reason === "size-exceeded") {
                                              setToastMessage("Maximum file size of 1MB");
                                            }
                                          } else {
                                            setToastMessage("Uploaded");
                                            setIsDirty(true); // Mark form as dirty immediately when banner image is uploaded
                                          }
                                        }}
                                      />
                                      <Button
                                        type="button"
                                        size="2"
                                        variant="surface"
                                        color="gray"
                                        style={{ display: "flex", padding: "0px var(--space-2)", justifyContent: "center", flex: "1" }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          bannerImage.triggerInput();
                                        }}
                                      >
                                        {bannerImage.preview ? (
                                          <Tooltip content="Upload new to replace">
                                            <Text>{bannerImage.fileName?.length >= 18 ? `${bannerImage.fileName.slice(0, 18)}...` : bannerImage.fileName}</Text>
                                          </Tooltip>
                                        ) : (
                                          <>
                                            <UploadIcon />
                                            <Text>Upload</Text>
                                          </>
                                        )}
                                      </Button>
                                      {bannerImage.preview && (
                                        <Button
                                          type="button"
                                          size="2"
                                          variant="surface"
                                          color="gray"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            bannerImage.handleClear();
                                            setIsDirty(true); // Mark form as dirty immediately when banner image is cleared
                                          }}
                                        >
                                          Clear
                                        </Button>
                                      )}
                                    </Flex>
                                    <Text size="1" weight="regular" color="gray">
                                      Recommended size of 96px by 96px. Maximum file size of 1MB.
                                    </Text>
                                  </Flex>
                                </Text>
                              </Flex>
                            )}
                          </Flex>
                        </Flex>
                      ) : (
                        // Redirect Tab
                        <Flex p="4" gap="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                          <Flex gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                            {/* Redirect URL */}
                            <Text as="label" size="2">
                              <Flex>
                                <Text size="2" weight="medium">
                                  Redirect URL
                                </Text>
                              </Flex>
                            </Text>
                            <TextField.Root size="2" value={redirectUrl} onChange={(e) => handleInputRedirectUrlChange(e.target.value)} style={{ alignSelf: "stretch" }} />
                          </Flex>
                          <Text size="1" weight="regular" color="gray">
                            If a URL is not provided, your landing page will be used as a fallback.
                          </Text>
                        </Flex>
                      )}
                      {segmentView === "redirect" && <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />}
                    </Tabs.Content>
                  </Tabs.Root>
                </Flex>
                {/* Period Selection Bar - unified for both single and multiple periods */}
                {/* {periodsData?.data?.length > 0 && (
                  <Flex direction="column" style={{ alignSelf: "stretch" }}>
                    <Separator size="4" orientation="horizontal" />
                    <Flex p="4" gap="2" direction="column" style={{ alignSelf: "stretch" }}>
                      {isLoadingPeriods ? (
                        <Flex p="2" gap="2" style={{ alignItems: "center", justifyContent: "center", borderRadius: "var(--radius-2)" }}>
                          <Spinner size="2" />
                        </Flex>
                      ) : (
                        <DraggablePeriodBar
                          periods={periodsData.data}
                          selectedPeriod={selectedPeriod}
                          handlePeriodClick={handlePeriodClick}
                          onPeriodsChanged={(updatedPeriods) => {
                            console.log("Periods updated after drag:", updatedPeriods);
                            setIsDirty(true);
                          }}
                        />
                      )}
                    </Flex>
                  </Flex>
                )} */}

                {/* Save Changes Button */}
                <Flex direction="column" style={{ alignSelf: "stretch", alignItems: "flex-end" }}>
                  <Separator size="4" orientation="horizontal" />
                  <Flex p="4" direction="column" style={{ alignSelf: "stretch", alignItems: "flex-start" }}>
                    <Button type="submit" variant="solid" loading={isSaveChangesLoading} style={{ alignSelf: "stretch", width: "100%" }} disabled={!isDirty}>
                      Save changes
                    </Button>
                  </Flex>
                </Flex>
              </Flex>
            </Form.Root>
          </Flex>
          {/* Mockup Preview Section */}
          <Flex
            gap="5"
            direction="column"
            style={{
              alignItems: "center",
              alignSelf: "stretch",
              flex: "1 0 0",
              borderRadius: "var(--radius-4)",
              justifyContent: "center",
              overflow: "hidden",
              border: "1px solid var(--gray-a6)",
              backgroundColor: "var(--color-panel-solid)",
            }}
          >
            {/* Mobile / Desktop */}
            {view === "mobile" ? (
              <IPhoneMockup screenWidth={screenWidthMobile} frameColor="var(--gray-12)" statusbarColor="var(--gray-contrast)" transparentNavBar="true" hideStatusBar="true" screenType="notch">
                <Suspense>
                  <CaptivePortalMockup
                    device={view}
                    scaleFactor={scaleFactorMobile}
                    logoPreview={logoPreview}
                    backgroundPreview={backgroundPreview}
                    backgroundOverlayColor={colorConversion(backgroundOverlayColor, backgroundOverlayOpacity)}
                    buttonColor={buttonColor}
                    buttonTextColor={buttonTextColor}
                    email={emailFlag}
                    age={ageFlag}
                    dob={dobFlag}
                    gender={genderFlag}
                    firstName={firstNameFlag}
                    lastName={lastNameFlag}
                    phoneNumber={phoneNumberFlag}
                    facebook={facebookFlag}
                    message={message}
                    activeTab={activeTab}
                    links_data={
                      showLinks
                        ? {
                            links: links,
                          }
                        : null
                    }
                    banner_data={
                      showBanner
                        ? {
                            title: bannerTitle,
                            description: bannerDescription,
                            banner_url: bannerLinkURL,
                            image: bannerImage.preview,
                            image_filename: bannerImage.fileName,
                          }
                        : null
                    }
                    segmentView={segmentView}
                    redirectUrl={redirectUrl}
                    orgDetails={orgDetails?.data}
                  />
                </Suspense>
              </IPhoneMockup>
            ) : (
              // Desktop View
              <Suspense>
                <Flex style={{ flex: "1 0 0", alignSelf: "stretch", justifyContent: "center" }}>
                  <CaptivePortalMockup
                    device={view}
                    logoPreview={logoPreview}
                    backgroundPreview={backgroundPreview}
                    backgroundOverlayColor={colorConversion(backgroundOverlayColor, backgroundOverlayOpacity)}
                    buttonColor={buttonColor}
                    buttonTextColor={buttonTextColor}
                    email={emailFlag}
                    age={ageFlag}
                    dob={dobFlag}
                    gender={genderFlag}
                    firstName={firstNameFlag}
                    lastName={lastNameFlag}
                    phoneNumber={phoneNumberFlag}
                    facebook={facebookFlag}
                    message={message}
                    activeTab={activeTab}
                    links_data={
                      showLinks
                        ? {
                            links: links,
                          }
                        : null
                    }
                    banner_data={
                      showBanner
                        ? {
                            title: bannerTitle,
                            description: bannerDescription,
                            banner_url: bannerLinkURL,
                            image: bannerImage.preview,
                            image_filename: bannerImage.fileName,
                          }
                        : null
                    }
                    segmentView={segmentView}
                    redirectUrl={redirectUrl}
                    orgDetails={orgDetails?.data}
                  />
                </Flex>
              </Suspense>
            )}
          </Flex>
        </Flex>
        {/* Toast Updates */}
        <ToastAlert />
      </React.Fragment>
    </Flex>
    // </Flex>
  );
};

const DraggablePeriodBar = ({ periods, selectedPeriod, handlePeriodClick, onPeriodsChanged }) => {
  // State to track periods with their start/end times
  const [adjustablePeriods, setAdjustablePeriods] = useState([]);
  // State to track if we're currently dragging
  const [isDragging, setIsDragging] = useState(false);
  // Ref to track which separator is being dragged
  const dragSeparatorIndex = useRef(null);
  // Ref to track the container element for calculations
  const containerRef = useRef(null);
  // Ref to track total width for calculations
  const totalWidthRef = useRef(0);

  // Check if buttons should be disabled
  const isAddDisabled = adjustablePeriods.length >= 3;
  const isDeleteDisabled = adjustablePeriods.length <= 1;

  // Default settings for new periods
  const defaultPeriodSettings = {
    background_overlay_colour: "#000000",
    background_overlay_opacity: 0.0,
    button_colour: "#3807D9",
    button_text_colour: "#FFFFFF",
    email_flag: true,
    age_flag: true,
    dob_flag: false,
    gender_flag: true,
    landing_type: "form", // Add default landing type
    privacy_type: "text", // Add default privacy type
    segment_view: "design", // Add default segment view
    facebook_flag: false, // Default Facebook flag
    privacy_policy: "", // Default empty privacy policy
    show_links: false, // Don't show links by default
    show_banner: false, // Don't show banner by default
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours, 10);

    // Special case for noon (12PM)
    if (hour === 12) {
      return "12PM";
    }

    // Special case for midnight (12AM)
    if (hour === 0) {
      return "12AM";
    }

    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;

    // For 11:59, show as 12PM
    if (hour === 11 && minutes === "59") {
      return "12PM";
    }

    // For 23:59, show as 12AM
    if (hour === 23 && minutes === "59") {
      return "12AM";
    }

    // For 3:59, show as 4AM in the 4AM-4AM cycle context
    if (hour === 3 && minutes === "59") {
      return "4AM";
    }

    // Simple hour + AM/PM format, no minutes
    return `${displayHour}${ampm}`;
  };

  // Normalize hour to the 4AM-to-4AM cycle
  const normalizeHour = (hour) => {
    // 4AM is the start of our day (0 in normalized space)
    // 3:59AM is the end of our day (23:59 in normalized space)
    return (hour - 4 + 24) % 24;
  };

  // Convert normalized hour back to standard 24-hour format
  const denormalizeHour = (normalizedHour) => {
    return (normalizedHour + 4) % 24;
  };

  // Handle drag movement
  const handleDragMove = (e) => {
    // Quick exit if we're not dragging
    if (!isDragging || dragSeparatorIndex.current === null || !containerRef.current) return;

    // Get mouse position relative to container
    const containerRect = containerRef.current.getBoundingClientRect();
    const mousePosition = e.clientX - containerRect.left;

    // Clamp position to container width
    const clampedPosition = Math.max(0, Math.min(mousePosition, containerRect.width));
    const positionPercent = clampedPosition / containerRect.width;

    // Map to our normalized 24-hour day (4AM-to-4AM)
    // Position 0% maps to 0 (4AM), position 100% maps to 24 (4AM next day)
    const normalizedHourPosition = Math.round(positionPercent * 24);

    // Update periods
    setAdjustablePeriods((prevPeriods) => {
      const newPeriods = [...prevPeriods];
      const index = dragSeparatorIndex.current;

      // Get the two periods being adjusted
      const leftPeriod = { ...newPeriods[index] };
      const rightPeriod = { ...newPeriods[index + 1] };

      // Special handling for key edge cases
      const is4AMStart = leftPeriod.start_time === "04:00";
      const is3AMTo4AMPeriod = rightPeriod.start_time === "03:00" && rightPeriod.end_time === "04:00";

      // Calculate valid drag range
      const minNormalizedHour = is4AMStart ? 1 : leftPeriod.normalizedStartHour + 1;

      let maxNormalizedHour;
      if (is3AMTo4AMPeriod) {
        maxNormalizedHour = 23; // 3AM in normalized time
      } else if (rightPeriod.end_time === "04:00") {
        maxNormalizedHour = 23; // Any period ending at 4AM
      } else {
        maxNormalizedHour = rightPeriod.normalizedEndHour - 1;
      }

      // Clamp to valid range
      const clampedNormalizedHour = Math.min(Math.max(normalizedHourPosition, minNormalizedHour), maxNormalizedHour);

      // Convert back to standard 24-hour clock
      const standardHour = denormalizeHour(clampedNormalizedHour);

      // Update the end time of the left period
      // Preserve the original minute format from the backend or use "00" if it was on the hour
      leftPeriod.end_time = `${standardHour.toString().padStart(2, "0")}:00`;
      leftPeriod.normalizedEndHour = clampedNormalizedHour;
      leftPeriod.duration = clampedNormalizedHour - leftPeriod.normalizedStartHour;
      leftPeriod.flexGrow = leftPeriod.duration;

      // Update the start time of the right period
      // Preserve the original minute format from the backend or use "00" if it was on the hour
      rightPeriod.start_time = `${standardHour.toString().padStart(2, "0")}:00`;
      rightPeriod.normalizedStartHour = clampedNormalizedHour;
      rightPeriod.duration = rightPeriod.normalizedEndHour - clampedNormalizedHour;
      rightPeriod.flexGrow = rightPeriod.duration;

      // Update the periods in the array
      newPeriods[index] = leftPeriod;
      newPeriods[index + 1] = rightPeriod;

      return newPeriods;
    });
  };

  useEffect(() => {
    if (periods && periods.length > 0) {
      // Handle single period "All day" case specially
      if (periods.length === 1) {
        const period = periods[0];

        // Extract only the essential properties needed for the UI
        const essentialProps = {
          id: period.id,
          guest_portal_setting: period.guest_portal_setting,
          plan: period.plan,
        };

        // For a single period, treat it as "All day" regardless of times
        const allDayPeriod = {
          ...essentialProps, // Only keep essential properties
          start_time: "04:00", // Standardize to 4AM
          end_time: "04:00", // Standardize to 4AM
          normalizedStartHour: 0,
          normalizedEndHour: 24,
          duration: 24,
          flexGrow: 24,
          isAllDay: true,
        };

        setAdjustablePeriods([allDayPeriod]);
        return;
      }

      // For multiple periods, sort them by start time
      const sortedPeriods = [...periods].sort((a, b) => {
        const aHour = parseInt(a.start_time.split(":")[0], 10);
        const bHour = parseInt(b.start_time.split(":")[0], 10);

        // Normalize to 4AM-to-4AM cycle
        const normalizedAHour = normalizeHour(aHour);
        const normalizedBHour = normalizeHour(bHour);

        return normalizedAHour - normalizedBHour;
      });

      // Calculate duration of each period in hours within the 4AM-to-4AM cycle
      const periodsWithDuration = sortedPeriods.map((period) => {
        // Extract only the essential properties needed for the UI
        const essentialProps = {
          id: period.id,
          guest_portal_setting: period.guest_portal_setting,
          plan: period.plan,
        };

        const startHour = parseInt(period.start_time.split(":")[0], 10);
        const endHour = parseInt(period.end_time.split(":")[0], 10);

        // Special case handling for edge cases
        const is4AMStart = startHour === 4;
        const is4AMEnd = endHour === 4;
        const is3AMTo4AMPeriod = startHour === 3 && endHour === 4;

        // Check if this is an "All day" period (starts at 4AM and ends at 4AM)
        const isAllDay = (startHour === 4 && endHour === 4) || (startHour === 4 && endHour === 0);

        // Normalize hours to 4AM-to-4AM cycle
        const normalizedStartHour = normalizeHour(startHour);
        let normalizedEndHour = normalizeHour(endHour);

        // For 4AM endpoint cases, we need special handling
        if ((is4AMEnd && !is4AMStart) || isAllDay) {
          // If period ends at 4AM or it's an all-day period, it's the end of our cycle
          normalizedEndHour = 24; // Use 24 instead of 0 for calculation purposes
        }
        // Handle periods that cross midnight within the 4AM-to-4AM cycle
        else if (normalizedEndHour < normalizedStartHour) {
          normalizedEndHour += 24;
        }

        const duration = normalizedEndHour - normalizedStartHour;

        return {
          ...essentialProps, // Only include essential properties
          start_time: period.start_time,
          end_time: period.end_time,
          normalizedStartHour,
          normalizedEndHour,
          duration,
          // Flex grow should be proportional to the duration
          flexGrow: duration,
          // Special flags for UI handling
          is4AMStart,
          is4AMEnd,
          is3AMTo4AMPeriod,
          isAllDay,
        };
      });

      setAdjustablePeriods(periodsWithDuration);
    }
  }, [periods]);

  const handleDragStart = (index, e) => {
    // Immediately set dragging state and store index
    setIsDragging(true);
    dragSeparatorIndex.current = index;

    // Get container dimensions immediately
    if (containerRef.current) {
      totalWidthRef.current = containerRef.current.getBoundingClientRect().width;
    }

    // Prevent default events
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Add event listeners
    document.addEventListener("mousemove", handleDragMove);
    document.addEventListener("mouseup", handleDragEnd);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);

    // Prevent text selection
    document.body.style.userSelect = "none";
  };

  // Add touch event handlers
  const handleTouchMove = (e) => {
    e.preventDefault(); // Prevent scrolling while dragging
    if (e.touches && e.touches[0]) {
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent("mousemove", {
        clientX: touch.clientX,
        clientY: touch.clientY,
        bubbles: true,
      });
      handleDragMove(mouseEvent);
    }
  };

  const handleTouchEnd = (e) => {
    const mouseEvent = new MouseEvent("mouseup", {
      bubbles: true,
    });
    handleDragEnd(mouseEvent);
    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("touchend", handleTouchEnd);
  };

  // Handle end of drag
  const handleDragEnd = () => {
    if (!isDragging) return;

    // Use current state for logging to ensure we have the latest values
    // Log the updated period times
    console.log("=== UPDATED Period times after drag ===");
    adjustablePeriods.forEach((period, index) => {
      console.log(
        `Period ${index + 1}: ${period.start_time} to ${period.end_time} (Display: ${formatTime(period.start_time)}-${formatTime(period.end_time)}, Duration: ${period.duration}h, Flex: ${
          period.flexGrow
        })`
      );
    });

    // Log the separator that was dragged
    if (dragSeparatorIndex.current !== null) {
      const leftPeriod = adjustablePeriods[dragSeparatorIndex.current];
      const rightPeriod = adjustablePeriods[dragSeparatorIndex.current + 1];
      console.log("Adjusted boundary between periods:");
      console.log(`Left period: ${leftPeriod.start_time} - ${leftPeriod.end_time} (${formatTime(leftPeriod.start_time)}-${formatTime(leftPeriod.end_time)})`);
      console.log(`Right period: ${rightPeriod.start_time} - ${rightPeriod.end_time} (${formatTime(rightPeriod.start_time)}-${formatTime(rightPeriod.end_time)})`);
      console.log(`New shared time: ${rightPeriod.start_time} (${formatTime(rightPeriod.start_time)})`);
    }

    // Make sure to update any parent components about the changes if needed
    // This is crucial to ensure the changes persist after dragging
    if (onPeriodsChanged) {
      onPeriodsChanged(adjustablePeriods);
    }

    setIsDragging(false);
    dragSeparatorIndex.current = null;

    // Remove event listeners
    document.removeEventListener("mousemove", handleDragMove);
    document.removeEventListener("mouseup", handleDragEnd);
    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("touchend", handleTouchEnd);

    // Re-enable text selection
    document.body.style.userSelect = "";
  };

  return (
    <Flex direction="row" gap="2" style={{ width: "100%", alignItems: "center" }}>
      {/* Period bar */}
      <Flex
        style={{
          width: "100%",
          borderRadius: "var(--radius-2)",
          overflow: "hidden",
          cursor: isDragging ? "col-resize" : "default",
          flex: 1,
        }}
        ref={containerRef}
      >
        {adjustablePeriods.length === 1 ? (
          // Single period "All day" display
          <Flex
            onClick={() => handlePeriodClick(adjustablePeriods[0])}
            style={{
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
              padding: "8px",
              height: "40px",
              cursor: "pointer",
              background: "var(--blue-a3)",
              border: "1px solid var(--blue-a6)",
              borderRadius: "var(--radius-2)",
            }}
          >
            <Text size="2" weight="regular">
              All day
            </Text>
          </Flex>
        ) : (
          // Multiple periods display
          adjustablePeriods.map((period, index, array) => {
            const startTime = formatTime(period.start_time);
            const endTime = formatTime(period.end_time);
            const isSelected = selectedPeriod?.id === period.id;
            const isFirst = index === 0;
            const isLast = index === array.length - 1;

            return (
              <React.Fragment key={period.id}>
                {/* Separator between periods (unchanged) */}
                {index > 0 && (
                  <div
                    role="button"
                    tabIndex={0}
                    style={{
                      width: "16px",
                      margin: "0 -8px",
                      height: "40px",
                      cursor: "col-resize",
                      zIndex: 10,
                      position: "relative",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      touchAction: "none",
                      backgroundColor: isDragging && dragSeparatorIndex.current === index - 1 ? "rgba(0, 120, 255, 0.1)" : "transparent",
                    }}
                    onMouseDown={(e) => handleDragStart(index - 1, e)}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      const touch = e.touches[0];
                      const mouseEvent = new MouseEvent("mousedown", {
                        clientX: touch.clientX,
                        clientY: touch.clientY,
                        bubbles: true,
                      });
                      handleDragStart(index - 1, mouseEvent);
                    }}
                  >
                    <div
                      style={{
                        width: "4px",
                        height: "100%",
                        background: isDragging && dragSeparatorIndex.current === index - 1 ? "var(--blue-9)" : "var(--gray-6)",
                        borderRadius: "2px",
                        pointerEvents: "none",
                      }}
                    />
                  </div>
                )}

                {/* Period button */}
                <Flex
                  onClick={() => handlePeriodClick(period)}
                  style={{
                    flex: period.flexGrow,
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "8px",
                    height: "40px",
                    cursor: isDragging ? "col-resize" : "pointer",
                    background: isSelected ? "var(--blue-a3)" : "var(--color-panel-solid)",
                    border: isSelected ? "1px solid var(--blue-a6)" : "1px solid var(--gray-a6)",
                    borderLeft: index > 0 ? "none" : isSelected ? "1px solid var(--blue-a6)" : "1px solid var(--gray-a6)",
                    borderRadius: isFirst && isLast ? "var(--radius-2)" : isFirst ? "var(--radius-2) 0 0 var(--radius-2)" : isLast ? "0 var(--radius-2) var(--radius-2) 0" : "0",
                  }}
                >
                  <Text size="2" weight="regular">
                    {startTime}-{endTime}
                  </Text>
                </Flex>
              </React.Fragment>
            );
          })
        )}
      </Flex>

      {/* Delete button */}
      <Tooltip content={isDeleteDisabled ? "At least one period is required" : adjustablePeriods.length === 2 ? "Convert to All Day" : "Delete Rightmost Period"}>
        <IconButton
          type="button"
          size="2"
          variant="soft"
          color="gray"
          disabled={isDeleteDisabled}
          style={{ opacity: isDeleteDisabled ? 0.5 : 1 }}
          onClick={() => {
            const periodsCount = adjustablePeriods.length;

            if (periodsCount <= 1) {
              // Can't delete if there's only one period
              return;
            }

            // Always delete the rightmost period (last in array)
            const rightmostPeriod = adjustablePeriods[adjustablePeriods.length - 1];

            if (periodsCount === 3) {
              // If there are three periods, remove the rightmost one and adjust the other two
              // Make a copy of the periods to work with
              let updatedPeriods = [...adjustablePeriods];

              // Remove the rightmost period
              updatedPeriods.pop();

              // Adjust the remaining two periods to split the day in half
              updatedPeriods[0] = {
                ...updatedPeriods[0],
                start_time: "04:00",
                end_time: "16:00",
                normalizedStartHour: 0, // 4AM is 0 in normalized time
                normalizedEndHour: 12, // 4PM is 12 in normalized time
                duration: 12,
                flexGrow: 12,
              };

              updatedPeriods[1] = {
                ...updatedPeriods[1],
                start_time: "16:00",
                end_time: "04:00",
                normalizedStartHour: 12, // 4PM is 12 in normalized time
                normalizedEndHour: 24, // 4AM next day is 24 in normalized time
                duration: 12,
                flexGrow: 12,
              };

              // Set the new periods array
              setAdjustablePeriods(updatedPeriods);

              // Select the rightmost period after deletion
              if (handlePeriodClick) {
                handlePeriodClick(updatedPeriods[updatedPeriods.length - 1]);
              }

              // Notify parent component of the changes
              if (onPeriodsChanged) {
                onPeriodsChanged(updatedPeriods);
              }
            } else if (periodsCount === 2) {
              // If there are two periods, convert to a single "All day" period
              // Use first period's ID but with all-day configuration
              const allDayPeriod = {
                // Use only the id and guest_portal_setting from the first period
                id: adjustablePeriods[0].id,
                guest_portal_setting: adjustablePeriods[0].guest_portal_setting,
                // Set all-day properties
                start_time: "04:00",
                end_time: "04:00", // Use 04:00 for both start and end to represent All day
                normalizedStartHour: 0, // 4AM is 0 in normalized time
                normalizedEndHour: 24, // 4AM next day is 24 in normalized time
                duration: 24,
                flexGrow: 24,
                isAllDay: true, // Mark as All day period
              };

              // Set the new periods array
              const newPeriods = [allDayPeriod];
              setAdjustablePeriods(newPeriods);

              // Select the all-day period
              if (handlePeriodClick) {
                handlePeriodClick(allDayPeriod);
              }

              // Notify parent component of the changes
              if (onPeriodsChanged) {
                onPeriodsChanged(newPeriods);
              }
            }
          }}
        >
          {/* Use a Radix trash icon instead of the inline SVG */}
          <TrashIcon width="15" height="15" />
        </IconButton>
      </Tooltip>

      {/* Add button */}
      <Tooltip content={isAddDisabled ? "Maximum 3 periods allowed" : adjustablePeriods.length === 1 ? "Split into Morning/Evening" : "Add Night Period"}>
        <IconButton
          type="button"
          size="2"
          variant="soft"
          color="blue"
          disabled={isAddDisabled}
          style={{ opacity: isAddDisabled ? 0.5 : 1 }}
          onClick={() => {
            // Add a new period with the correct time distribution
            const updatedPeriods = [...adjustablePeriods];
            const periodsCount = updatedPeriods.length;

            // Get the guest portal setting ID from an existing period
            const guestPortalSettingId = updatedPeriods[0]?.guest_portal_setting;

            if (periodsCount === 1) {
              // If there's only one period (All Day), split it into two equal periods
              const allDayPeriod = updatedPeriods[0];

              // Create two periods of 12 hours each (splitting the day in half)
              const firstHalf = {
                // Only include minimal required properties from the existing period
                id: allDayPeriod.id, // Keep the original ID
                guest_portal_setting: guestPortalSettingId, // Keep reference to the portal setting
                plan: allDayPeriod.plan, // Keep plan reference if it exists

                // Set the timing properties
                start_time: "04:00",
                end_time: "16:00",
                normalizedStartHour: 0, // 4AM is 0 in normalized time
                normalizedEndHour: 12, // 4PM is 12 in normalized time
                duration: 12,
                flexGrow: 12,
              };

              const secondHalf = {
                // Create a completely new period with default settings
                ...defaultPeriodSettings,
                id: `temp_period_2_${Date.now()}`, // Temporary ID until saved
                guest_portal_setting: guestPortalSettingId, // Reference to the portal setting
                plan: allDayPeriod.plan, // Keep plan reference if it exists

                // Set the timing properties
                start_time: "16:00",
                end_time: "04:00",
                normalizedStartHour: 12, // 4PM is 12 in normalized time
                normalizedEndHour: 24, // 4AM next day is 24 in normalized time
                duration: 12,
                flexGrow: 12,
              };

              // Set the new periods array
              const newPeriods = [firstHalf, secondHalf];
              setAdjustablePeriods(newPeriods);

              // Select the newly created period at the extreme right
              if (handlePeriodClick) {
                handlePeriodClick(secondHalf);
              }

              // Notify parent component of the changes
              if (onPeriodsChanged) {
                onPeriodsChanged(newPeriods);
              }
            } else if (periodsCount === 2) {
              // If there are two periods, split them into three equal periods (8 hours each)

              // Create three periods with equal distribution (8 hours each)
              // Keep the first two periods (may have user settings) but adjust their times
              const firstPeriod = {
                // Preserve the original period, just change time properties
                ...updatedPeriods[0],
                start_time: "04:00",
                end_time: "12:00",
                normalizedStartHour: 0, // 4AM is 0 in normalized time
                normalizedEndHour: 8, // 12PM is 8 in normalized time
                duration: 8,
                flexGrow: 8,
              };

              const secondPeriod = {
                // Preserve the original period, just change time properties
                ...updatedPeriods[1],
                start_time: "12:00",
                end_time: "20:00",
                normalizedStartHour: 8, // 12PM is 8 in normalized time
                normalizedEndHour: 16, // 8PM is 16 in normalized time
                duration: 8,
                flexGrow: 8,
              };

              // Create a new third period with default settings
              const thirdPeriod = {
                ...defaultPeriodSettings, // Apply all default settings
                id: `temp_period_3_${Date.now()}`, // New temporary ID
                guest_portal_setting: guestPortalSettingId, // Reference to the portal setting
                plan: updatedPeriods[0].plan, // Keep plan reference if it exists

                // Set timing properties
                start_time: "20:00",
                end_time: "04:00",
                normalizedStartHour: 16, // 8PM is 16 in normalized time
                normalizedEndHour: 24, // 4AM next day is 24 in normalized time
                duration: 8,
                flexGrow: 8,
              };

              // Set the new periods array
              const newPeriods = [firstPeriod, secondPeriod, thirdPeriod];
              setAdjustablePeriods(newPeriods);

              // Select the newly created period (the rightmost one)
              if (handlePeriodClick) {
                handlePeriodClick(thirdPeriod);
              }

              // Notify parent component of the changes
              if (onPeriodsChanged) {
                onPeriodsChanged(newPeriods);
              }
            }
          }}
        >
          {/* Use Radix PlusIcon */}
          <PlusIcon width="15" height="15" />
        </IconButton>
      </Tooltip>
    </Flex>
  );
};

export default GuestPortal;
